import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { GROWTHOS_BASE } from "../config/api";
import {
  Image as ImageIcon,
  Sparkles,
  FileText,
  ArrowLeft,
  Upload,
  X,
  Download,
  Loader2,
  AlertCircle,
  Phone,
  Mail,
  Globe,
  MapPin,
  ChevronDown,
  Palette,
  Check,
  ArrowUpRight,
  Wand2,
  History,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  Columns,
  Building2,
  DollarSign,
  Quote,
  ImagePlus,
  Rocket,
  Layers,
  Hash
} from "lucide-react";
// Feature-card/flyer generation now lives in the SAME backend as
// everything else (main.py) — there is no separate feature-card
// server anymore. Base URL comes from the shared config now.
const GROWTHOS_API_BASE = GROWTHOS_BASE;
const AI_THEME_ENDPOINT = `${GROWTHOS_API_BASE}/api/generate-theme`;
const FLYER_AI_CONTENT_ENDPOINT = `${GROWTHOS_API_BASE}/api/generate-flyer-content`;
const FONT_OPTIONS = [
  { id: "inter", label: "Inter", stack: "'Inter', sans-serif", google: "Inter:wght@400;600;700;900" },
  { id: "poppins", label: "Poppins", stack: "'Poppins', sans-serif", google: "Poppins:wght@400;600;700;900" },
  { id: "montserrat", label: "Montserrat", stack: "'Montserrat', sans-serif", google: "Montserrat:wght@400;600;700;900" },
  { id: "dmsans", label: "DM Sans", stack: "'DM Sans', sans-serif", google: "DM+Sans:wght@400;600;700;900" },
  { id: "spacegrotesk", label: "Space Grotesk", stack: "'Space Grotesk', sans-serif", google: "Space+Grotesk:wght@400;600;700" },
  { id: "playfair", label: "Playfair Display", stack: "'Playfair Display', serif", google: "Playfair+Display:wght@400;600;700;900" },
  { id: "lora", label: "Lora", stack: "'Lora', serif", google: "Lora:wght@400;600;700" },
  { id: "merriweather", label: "Merriweather", stack: "'Merriweather', serif", google: "Merriweather:wght@400;700;900" },
  { id: "timesnewroman", label: "Times New Roman", stack: "'Times New Roman', Times, serif", google: null },
  // "Other" has no fixed stack — picking it reveals a text input where
  // the person types any font name they want (system font or a Google
  // Font name); whatever they type becomes the font-family directly.
  { id: "other", label: "Other", stack: null, google: null }
];
const useGoogleFonts = (families) => {
  const familiesKey = families.filter(Boolean).join("|");
  useEffect(() => {
    const wanted = familiesKey ? familiesKey.split("|") : [];
    if (!wanted.length) return;
    const href = `https://fonts.googleapis.com/css2?${wanted.map((f) => `family=${f}`).join("&")}&display=swap`;
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }, [familiesKey]);
};
const SAVED_THEMES_ENDPOINT = `${GROWTHOS_API_BASE}/saved-themes`;
const ME_ENDPOINT = `${GROWTHOS_API_BASE}/auth/me`;
const PRODUCTS_ENDPOINT = `${GROWTHOS_API_BASE}/products`;
const AUTH_TOKEN_KEY = "growthos_token";
const authHeaders = () => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  return {
    "Content-Type": "application/json",
    ...token ? { Authorization: `Bearer ${token}` } : {}
  };
};
const Field = ({ label, required, children }) => /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("label", { className: "text-xs font-semibold text-gray-400 tracking-wide uppercase" }, label, " ", required && /* @__PURE__ */ React.createElement("span", { className: "text-orange-500" }, "*")), /* @__PURE__ */ React.createElement("div", { className: "mt-2" }, children));
const inputClass = "w-full bg-white/[0.04] border border-white/10 text-white text-sm rounded-xl px-4 py-2.5 outline-none transition focus:border-orange-500/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-orange-500/10 placeholder:text-gray-600";
const IconInput = ({ icon: Icon, className = "", ...props }) => /* @__PURE__ */ React.createElement("div", { className: "relative" }, /* @__PURE__ */ React.createElement(Icon, { size: 15, className: "absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" }), /* @__PURE__ */ React.createElement("input", { ...props, className: `${inputClass} pl-10 ${className}` }));
const SectionCard = ({ icon: Icon, title, subtitle, action, children }) => /* @__PURE__ */ React.createElement("div", { className: "rounded-2xl border border-white/5 bg-white/[0.02] p-5" }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center justify-between mb-4" }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2.5" }, /* @__PURE__ */ React.createElement("div", { className: "h-8 w-8 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0" }, /* @__PURE__ */ React.createElement(Icon, { size: 15, className: "text-orange-500" })), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h4", { className: "text-white text-sm font-semibold leading-tight" }, title), subtitle && /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 text-xs mt-0.5" }, subtitle))), action), /* @__PURE__ */ React.createElement("div", { className: "space-y-4" }, children));
const MAX_FEATURES = 8;
const emptyFeature = () => ({ title: "", description: "" });
const CardLogo = ({ src, size = 56, tone = "light" }) => {
  const toneClasses = tone === "light" ? "bg-gray-50 border-gray-200" : tone === "dark" ? "bg-white/5 border-white/15" : "bg-white/90 border-white/40";
  const iconColor = tone === "light" ? "text-gray-300" : tone === "dark" ? "text-white/30" : "text-orange-500/50";
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: `rounded-xl border flex items-center justify-center overflow-hidden shrink-0 ${toneClasses}`,
      style: { height: size, width: size }
    },
    src ? /* @__PURE__ */ React.createElement("img", { src, alt: "Logo", className: "h-full w-full object-cover object-center" }) : /* @__PURE__ */ React.createElement(ImageIcon, { size: Math.round(size * 0.36), className: iconColor })
  );
};
const ClassicSplitTheme = ({ logoPreview, productName, tagline, contactRows, features, bottomStatement }) => /* @__PURE__ */ React.createElement("div", { className: "flex flex-col sm:flex-row" }, /* @__PURE__ */ React.createElement("div", { className: "w-full min-w-0 sm:w-[38%] bg-white p-6 flex flex-col gap-5" }, /* @__PURE__ */ React.createElement(CardLogo, { src: logoPreview, tone: "light" }), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h3", { className: "text-gray-900 font-extrabold text-2xl leading-tight break-words" }, productName || "Your Product Name"), /* @__PURE__ */ React.createElement("div", { className: "h-1 w-10 bg-orange-500 rounded-full mt-2.5" })), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col gap-3.5 mt-1" }, contactRows.map(({ Icon, value, placeholder }, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-center gap-3 min-w-0" }, /* @__PURE__ */ React.createElement("div", { className: "h-7 w-7 rounded-full bg-orange-500 flex items-center justify-center shrink-0" }, /* @__PURE__ */ React.createElement(Icon, { size: 12, className: "text-white" })), /* @__PURE__ */ React.createElement("span", { className: "text-gray-800 text-xs font-medium leading-snug min-w-0 flex-1 [overflow-wrap:anywhere]" }, value || placeholder))))), /* @__PURE__ */ React.createElement("div", { className: "relative w-full sm:w-[62%] bg-[#0a0a0a] p-6 flex flex-col justify-between overflow-hidden" }, /* @__PURE__ */ React.createElement(
  "div",
  {
    className: "absolute -top-20 -right-20 h-64 w-64 rounded-full pointer-events-none",
    style: { background: "rgba(255,107,0,0.10)" }
  }
), /* @__PURE__ */ React.createElement("div", { className: "relative z-10" }, /* @__PURE__ */ React.createElement("p", { className: "text-white text-[13px] font-extrabold tracking-[0.15em] uppercase" }, tagline || "Smart. Simple. AI-Powered."), /* @__PURE__ */ React.createElement("div", { className: "h-[3px] w-16 bg-orange-500 rounded-full mt-3 mb-6" }), /* @__PURE__ */ React.createElement("div", { className: "relative" }, features.length === 4 && /* @__PURE__ */ React.createElement("div", { className: "absolute inset-0 pointer-events-none hidden sm:block" }, /* @__PURE__ */ React.createElement("div", { className: "absolute left-1/2 top-2 bottom-2 w-px bg-white/10 -translate-x-1/2" }), /* @__PURE__ */ React.createElement("div", { className: "absolute top-1/2 left-2 right-2 h-px bg-white/10 -translate-y-1/2" }), /* @__PURE__ */ React.createElement("div", { className: "absolute left-1/2 top-1/2 h-2 w-2 rounded-full bg-orange-500 -translate-x-1/2 -translate-y-1/2" })), /* @__PURE__ */ React.createElement("div", { className: "relative z-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6" }, features.length > 0 ? features.map((f, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-start gap-2.5" }, /* @__PURE__ */ React.createElement("div", { className: "h-9 w-9 rounded-full bg-orange-500/10 border-2 border-orange-500 flex items-center justify-center shrink-0" }, /* @__PURE__ */ React.createElement(Sparkles, { size: 14, className: "text-orange-500" })), /* @__PURE__ */ React.createElement("div", { className: "min-w-0" }, /* @__PURE__ */ React.createElement("p", { className: "text-white text-[13px] font-bold leading-snug" }, f.title), f.description && /* @__PURE__ */ React.createElement("p", { className: "text-gray-400 text-[11px] leading-snug mt-0.5" }, f.description)))) : /* @__PURE__ */ React.createElement("p", { className: "text-gray-600 text-xs col-span-2" }, "Add 3\u20134 feature highlights to see them here.")))), bottomStatement && /* @__PURE__ */ React.createElement("div", { className: "relative z-10 mt-6 bg-white rounded-xl px-5 py-3.5 text-center" }, /* @__PURE__ */ React.createElement("p", { className: "text-gray-900 text-[13px] font-bold" }, bottomStatement))));
const BlueSplitTheme = ({ logoPreview, productName, tagline, contactRows, features, bottomStatement }) => /* @__PURE__ */ React.createElement("div", { className: "flex flex-col sm:flex-row" }, /* @__PURE__ */ React.createElement("div", { className: "w-full min-w-0 sm:w-[36%] bg-white p-6 flex flex-col gap-5" }, /* @__PURE__ */ React.createElement(CardLogo, { src: logoPreview, tone: "light" }), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h3", { className: "text-gray-900 font-extrabold text-2xl leading-tight break-words" }, productName || "Your Product Name"), tagline && /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 text-[11px] mt-1 break-words" }, tagline), /* @__PURE__ */ React.createElement("div", { className: "h-1 w-10 bg-blue-600 rounded-full mt-2.5" })), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col gap-3.5 mt-1" }, contactRows.map(({ Icon, value, placeholder }, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-center gap-3 min-w-0" }, /* @__PURE__ */ React.createElement("div", { className: "h-7 w-7 rounded-full bg-blue-600 flex items-center justify-center shrink-0" }, /* @__PURE__ */ React.createElement(Icon, { size: 12, className: "text-white" })), /* @__PURE__ */ React.createElement("span", { className: "text-gray-700 text-xs font-medium leading-snug min-w-0 flex-1 [overflow-wrap:anywhere]" }, value || placeholder))))), /* @__PURE__ */ React.createElement(
  "div",
  {
    className: "relative w-full sm:w-[64%] p-6 flex flex-col justify-between overflow-hidden",
    style: { background: "linear-gradient(155deg, #3b82f6 0%, #1d4ed8 55%, #1e3a8a 100%)" }
  },
  /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("p", { className: "text-white text-[13px] font-extrabold tracking-[0.15em] uppercase" }, "Our Key Features"), /* @__PURE__ */ React.createElement("div", { className: "h-[3px] w-16 bg-white/60 rounded-full mt-3 mb-5" }), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col gap-3.5" }, features.length > 0 ? features.map((f, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-start gap-3" }, /* @__PURE__ */ React.createElement("div", { className: "h-10 w-10 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0" }, /* @__PURE__ */ React.createElement(Sparkles, { size: 16, className: "text-white" })), /* @__PURE__ */ React.createElement("div", { className: "min-w-0" }, /* @__PURE__ */ React.createElement("p", { className: "text-white text-[13px] font-bold leading-snug" }, f.title), f.description && /* @__PURE__ */ React.createElement("p", { className: "text-blue-100/80 text-[11px] leading-snug mt-0.5" }, f.description)))) : /* @__PURE__ */ React.createElement("p", { className: "text-blue-100/70 text-xs" }, "Add 3\u20134 feature highlights to see them here."))),
  bottomStatement && /* @__PURE__ */ React.createElement("div", { className: "mt-6 bg-white rounded-xl px-5 py-3.5 text-center" }, /* @__PURE__ */ React.createElement("p", { className: "text-blue-700 text-[13px] font-bold" }, bottomStatement))
));
const PurpleTimelineTheme = ({ logoPreview, productName, tagline, contactRows, features, bottomStatement }) => /* @__PURE__ */ React.createElement(
  "div",
  {
    className: "flex flex-col p-6 sm:p-7",
    style: {
      background: "radial-gradient(circle at 15% 15%, #2e1065 0%, #0b0620 55%, #05030f 100%)",
      minHeight: 380
    }
  },
  /* @__PURE__ */ React.createElement("div", { className: "flex flex-col sm:flex-row gap-6 flex-1" }, /* @__PURE__ */ React.createElement("div", { className: "w-full min-w-0 sm:w-[38%] flex flex-col gap-5" }, /* @__PURE__ */ React.createElement(
    "div",
    {
      className: "h-16 w-16 border-2 border-purple-500/50 bg-white/5 flex items-center justify-center overflow-hidden shrink-0",
      style: { clipPath: "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)" }
    },
    logoPreview ? /* @__PURE__ */ React.createElement("img", { src: logoPreview, alt: "Logo", className: "h-full w-full object-cover object-center" }) : /* @__PURE__ */ React.createElement(ImageIcon, { size: 20, className: "text-purple-300/50" })
  ), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h3", { className: "text-white font-extrabold text-2xl leading-tight break-words" }, productName || "Your Product Name"), tagline && /* @__PURE__ */ React.createElement("p", { className: "text-gray-400 text-[11px] mt-1.5 break-words" }, tagline), /* @__PURE__ */ React.createElement("div", { className: "h-[3px] w-10 bg-purple-500 rounded-full mt-2.5" })), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col gap-3 mt-1" }, contactRows.map(({ Icon, value, placeholder }, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-center gap-3 min-w-0" }, /* @__PURE__ */ React.createElement("div", { className: "h-7 w-7 rounded-full bg-purple-600 flex items-center justify-center shrink-0" }, /* @__PURE__ */ React.createElement(Icon, { size: 12, className: "text-white" })), /* @__PURE__ */ React.createElement("span", { className: "text-gray-300 text-xs font-medium leading-snug min-w-0 flex-1 [overflow-wrap:anywhere]" }, value || placeholder))))), /* @__PURE__ */ React.createElement("div", { className: "w-full sm:w-[62%]" }, /* @__PURE__ */ React.createElement("p", { className: "text-white text-[13px] font-extrabold tracking-[0.15em] uppercase" }, "Feature Highlights"), /* @__PURE__ */ React.createElement("div", { className: "h-[3px] w-12 bg-purple-500 rounded-full mt-3 mb-5" }), /* @__PURE__ */ React.createElement("div", { className: "relative flex flex-col gap-3.5" }, /* @__PURE__ */ React.createElement("div", { className: "absolute left-[19px] top-2 bottom-2 w-px bg-purple-500/30 hidden sm:block" }), features.length > 0 ? features.map((f, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "relative flex items-start gap-3.5" }, /* @__PURE__ */ React.createElement("div", { className: "relative z-10 h-10 w-10 rounded-xl bg-purple-950 border border-purple-500/50 flex items-center justify-center shrink-0" }, /* @__PURE__ */ React.createElement(Sparkles, { size: 15, className: "text-purple-300" })), /* @__PURE__ */ React.createElement("div", { className: "min-w-0 flex-1 bg-white/[0.03] border border-purple-500/20 rounded-xl px-4 py-2.5" }, /* @__PURE__ */ React.createElement("p", { className: "text-white text-[13px] font-bold leading-snug" }, f.title), f.description && /* @__PURE__ */ React.createElement("p", { className: "text-gray-400 text-[11px] leading-snug mt-0.5" }, f.description)))) : /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 text-xs" }, "Add 3\u20134 feature highlights to see them here.")))),
  bottomStatement && /* @__PURE__ */ React.createElement(
    "div",
    {
      className: "mt-6 rounded-xl px-5 py-3.5 text-center",
      style: { background: "linear-gradient(90deg, #7c3aed, #a855f7)" }
    },
    /* @__PURE__ */ React.createElement("p", { className: "text-white text-[13px] font-bold" }, bottomStatement)
  )
);
const NatureGreenTheme = ({ logoPreview, productName, tagline, contactRows, features, bottomStatement }) => /* @__PURE__ */ React.createElement(
  "div",
  {
    className: "relative flex flex-col sm:flex-row overflow-hidden",
    style: { minHeight: 380, background: "linear-gradient(160deg, #eef4ea 0%, #dce9d8 100%)" }
  },
  /* @__PURE__ */ React.createElement(
    "div",
    {
      className: "absolute -bottom-16 -left-10 h-56 w-56 rounded-full pointer-events-none",
      style: { background: "rgba(74,124,89,0.14)" }
    }
  ),
  /* @__PURE__ */ React.createElement(
    "div",
    {
      className: "absolute top-10 right-10 h-24 w-24 rounded-full pointer-events-none hidden sm:block",
      style: { background: "rgba(74,124,89,0.10)" }
    }
  ),
  /* @__PURE__ */ React.createElement("div", { className: "relative z-10 w-full min-w-0 sm:w-[36%] p-6 flex flex-col gap-5" }, /* @__PURE__ */ React.createElement(CardLogo, { src: logoPreview, tone: "light" }), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h3", { className: "font-extrabold text-2xl leading-tight break-words", style: { color: "#2f5233" } }, productName || "Your Product Name"), tagline && /* @__PURE__ */ React.createElement("p", { className: "text-gray-600 text-[11px] mt-1 break-words" }, tagline), /* @__PURE__ */ React.createElement("div", { className: "h-1 w-10 rounded-full mt-2.5", style: { background: "#4a7c59" } })), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col gap-3.5 mt-1" }, contactRows.map(({ Icon, value, placeholder }, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-center gap-3 min-w-0" }, /* @__PURE__ */ React.createElement(
    "div",
    {
      className: "h-7 w-7 rounded-full flex items-center justify-center shrink-0",
      style: { background: "#4a7c59" }
    },
    /* @__PURE__ */ React.createElement(Icon, { size: 12, className: "text-white" })
  ), /* @__PURE__ */ React.createElement("span", { className: "text-gray-700 text-xs font-medium leading-snug min-w-0 flex-1 [overflow-wrap:anywhere]" }, value || placeholder))))),
  /* @__PURE__ */ React.createElement("div", { className: "relative z-10 w-full sm:w-[64%] p-6 flex flex-col justify-between" }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("p", { className: "text-[13px] font-extrabold tracking-[0.15em] uppercase", style: { color: "#2f5233" } }, "Feature Highlights"), /* @__PURE__ */ React.createElement("div", { className: "h-[3px] w-14 rounded-full mt-3 mb-5", style: { background: "#4a7c59" } }), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col gap-3" }, features.length > 0 ? features.map((f, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-start gap-3 bg-white/80 rounded-2xl px-4 py-3 shadow-sm" }, /* @__PURE__ */ React.createElement(
    "div",
    {
      className: "h-9 w-9 rounded-full flex items-center justify-center shrink-0",
      style: { background: "rgba(74,124,89,0.15)" }
    },
    /* @__PURE__ */ React.createElement(Sparkles, { size: 14, style: { color: "#4a7c59" } })
  ), /* @__PURE__ */ React.createElement("div", { className: "min-w-0" }, /* @__PURE__ */ React.createElement("p", { className: "text-gray-900 text-[13px] font-bold leading-snug" }, f.title), f.description && /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 text-[11px] leading-snug mt-0.5" }, f.description)))) : /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 text-xs" }, "Add 3\u20134 feature highlights to see them here."))), bottomStatement && /* @__PURE__ */ React.createElement(
    "div",
    {
      className: "mt-5 rounded-xl px-5 py-3.5 text-center",
      style: { background: "linear-gradient(90deg, #2f5233, #4a7c59)" }
    },
    /* @__PURE__ */ React.createElement("p", { className: "text-white text-[13px] font-bold" }, bottomStatement)
  ))
);
const OrangeHighlightTheme = ({ logoPreview, productName, tagline, contactRows, features, bottomStatement }) => /* @__PURE__ */ React.createElement("div", { className: "relative flex flex-col bg-white overflow-hidden", style: { minHeight: 380 } }, /* @__PURE__ */ React.createElement("div", { className: "absolute top-5 right-5 grid grid-cols-4 gap-1.5 pointer-events-none hidden sm:grid" }, Array.from({ length: 16 }).map((_, i) => /* @__PURE__ */ React.createElement("span", { key: i, className: "h-1 w-1 rounded-full bg-orange-300" }))), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col sm:flex-row flex-1 p-6 gap-6" }, /* @__PURE__ */ React.createElement("div", { className: "w-full min-w-0 sm:w-[34%] flex flex-col gap-5" }, /* @__PURE__ */ React.createElement(CardLogo, { src: logoPreview, tone: "light" }), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h3", { className: "text-gray-900 font-extrabold text-2xl leading-tight break-words" }, productName || "Your Product Name"), tagline && /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 text-[11px] mt-1 break-words" }, tagline), /* @__PURE__ */ React.createElement("div", { className: "h-1 w-10 bg-orange-500 rounded-full mt-2.5" })), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col gap-3.5 mt-1" }, contactRows.map(({ Icon, value, placeholder }, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-center gap-3 min-w-0" }, /* @__PURE__ */ React.createElement("div", { className: "h-7 w-7 rounded-full bg-orange-500 flex items-center justify-center shrink-0" }, /* @__PURE__ */ React.createElement(Icon, { size: 12, className: "text-white" })), /* @__PURE__ */ React.createElement("span", { className: "text-gray-700 text-xs font-medium leading-snug min-w-0 flex-1 [overflow-wrap:anywhere]" }, value || placeholder))))), /* @__PURE__ */ React.createElement("div", { className: "w-full sm:w-[66%]" }, /* @__PURE__ */ React.createElement("p", { className: "text-orange-500 text-[13px] font-extrabold tracking-[0.15em] uppercase" }, "Feature Highlights"), /* @__PURE__ */ React.createElement("div", { className: "h-[3px] w-14 bg-orange-500 rounded-full mt-3 mb-5" }), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col gap-3" }, features.length > 0 ? features.map((f, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-start gap-3 border-b border-orange-100 pb-3 last:border-b-0" }, /* @__PURE__ */ React.createElement("div", { className: "h-10 w-10 rounded-full bg-orange-50 border-2 border-orange-500 flex items-center justify-center shrink-0" }, /* @__PURE__ */ React.createElement(Sparkles, { size: 15, className: "text-orange-500" })), /* @__PURE__ */ React.createElement("div", { className: "min-w-0" }, /* @__PURE__ */ React.createElement("p", { className: "text-gray-900 text-[13px] font-bold leading-snug" }, f.title), f.description && /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 text-[11px] leading-snug mt-0.5" }, f.description)))) : /* @__PURE__ */ React.createElement("p", { className: "text-gray-400 text-xs" }, "Add 3\u20134 feature highlights to see them here.")))), bottomStatement && /* @__PURE__ */ React.createElement(
  "div",
  {
    className: "px-6 py-3.5 text-center",
    style: { background: "#f97316", clipPath: "polygon(0 0, 96% 0, 100% 100%, 0% 100%)" }
  },
  /* @__PURE__ */ React.createElement("p", { className: "text-white text-[13px] font-bold" }, bottomStatement)
));
const CARD_THEMES = [
  { id: "classic", label: "Split Classic", Component: ClassicSplitTheme, swatch: "linear-gradient(90deg, #ffffff 50%, #0a0a0a 50%)" },
  { id: "blue", label: "Blue Gradient", Component: BlueSplitTheme, swatch: "linear-gradient(155deg, #3b82f6 0%, #1e3a8a 100%)" },
  { id: "purple", label: "Purple Timeline", Component: PurpleTimelineTheme, swatch: "radial-gradient(circle at 30% 30%, #7c3aed 0%, #1e0a3c 100%)" },
  { id: "nature", label: "Nature Green", Component: NatureGreenTheme, swatch: "linear-gradient(160deg, #eef4ea 0%, #4a7c59 100%)" },
  { id: "amber", label: "Orange Highlights", Component: OrangeHighlightTheme, swatch: "linear-gradient(160deg, #ffffff 0%, #f97316 100%)" }
];
const SHADOW_PRESETS = {
  none: "none",
  soft: "0 25px 60px -20px rgba(0,0,0,0.35)",
  medium: "0 30px 80px -20px rgba(0,0,0,0.5)",
  hard: "10px 10px 0 0 rgba(0,0,0,0.85)"
};
const DynamicThemeCard = ({ theme, logoPreview, productName, tagline, contactRows, features, bottomStatement }) => {
  const {
    background = "linear-gradient(135deg, #111111 0%, #1a1a1a 100%)",
    accent = "#f97316",
    text = "#ffffff",
    cardStyle = "solid",
    // "solid" | "glass" | "minimal"
    borderRadius = 20,
    shadow = "soft",
    // "none" | "soft" | "medium" | "hard"
    font = "inherit",
    layout = "split",
    // "split" | "stack"
    featureLayout = "grid",
    // "grid" | "list"
    iconStyle = "circle"
    // "circle" | "outline" | "square"
  } = theme || {};
  const isGlass = cardStyle === "glass";
  const isStack = layout === "stack";
  const leftPanelStyle = isGlass ? { background: "rgba(255,255,255,0.85)", backdropFilter: "blur(16px)" } : { background: "#ffffff" };
  const rightPanelStyle = { background, color: text, ...isGlass ? { backdropFilter: "blur(16px)" } : {} };
  const iconWrapClass = iconStyle === "outline" ? "rounded-full border-2 bg-transparent" : iconStyle === "square" ? "rounded-lg" : "rounded-full border-2";
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: `flex ${isStack ? "flex-col" : "flex-col sm:flex-row"} overflow-hidden`,
      style: { borderRadius, boxShadow: SHADOW_PRESETS[shadow] ?? SHADOW_PRESETS.soft, fontFamily: font }
    },
    /* @__PURE__ */ React.createElement("div", { className: `w-full min-w-0 ${isStack ? "" : "sm:w-[38%]"} p-6 flex flex-col gap-5`, style: leftPanelStyle }, /* @__PURE__ */ React.createElement(CardLogo, { src: logoPreview, tone: "light" }), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h3", { className: "font-extrabold text-2xl leading-tight break-words text-gray-900" }, productName || "Your Product Name"), tagline && /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 text-[11px] mt-1 break-words" }, tagline), /* @__PURE__ */ React.createElement("div", { className: "h-1 w-10 rounded-full mt-2.5", style: { background: accent } })), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col gap-3.5 mt-1" }, contactRows.map(({ Icon, value, placeholder }, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-center gap-3 min-w-0" }, /* @__PURE__ */ React.createElement("div", { className: "h-7 w-7 rounded-full flex items-center justify-center shrink-0", style: { background: accent } }, /* @__PURE__ */ React.createElement(Icon, { size: 12, className: "text-white" })), /* @__PURE__ */ React.createElement("span", { className: "text-gray-800 text-xs font-medium leading-snug min-w-0 flex-1 [overflow-wrap:anywhere]" }, value || placeholder))))),
    /* @__PURE__ */ React.createElement("div", { className: `relative w-full ${isStack ? "" : "sm:w-[62%]"} p-6 flex flex-col justify-between overflow-hidden`, style: rightPanelStyle }, /* @__PURE__ */ React.createElement("div", { className: "absolute -top-20 -right-20 h-64 w-64 rounded-full pointer-events-none", style: { background: `${accent}22` } }), /* @__PURE__ */ React.createElement("div", { className: "relative z-10" }, /* @__PURE__ */ React.createElement("p", { className: "text-[13px] font-extrabold tracking-[0.15em] uppercase", style: { color: text } }, tagline || "Why Choose Us"), /* @__PURE__ */ React.createElement("div", { className: "h-[3px] w-16 rounded-full mt-3 mb-6", style: { background: accent } }), /* @__PURE__ */ React.createElement("div", { className: featureLayout === "list" ? "flex flex-col gap-3.5" : "grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6" }, features.length > 0 ? features.map((f, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-start gap-2.5" }, /* @__PURE__ */ React.createElement(
      "div",
      {
        className: `h-9 w-9 flex items-center justify-center shrink-0 ${iconWrapClass}`,
        style: {
          borderColor: accent,
          background: iconStyle === "square" ? accent : `${accent}1a`,
          color: iconStyle === "square" ? "#ffffff" : accent
        }
      },
      /* @__PURE__ */ React.createElement(Sparkles, { size: 14 })
    ), /* @__PURE__ */ React.createElement("div", { className: "min-w-0" }, /* @__PURE__ */ React.createElement("p", { className: "text-[13px] font-bold leading-snug", style: { color: text } }, f.title), f.description && /* @__PURE__ */ React.createElement("p", { className: "text-[11px] leading-snug mt-0.5 opacity-70", style: { color: text } }, f.description)))) : /* @__PURE__ */ React.createElement("p", { className: "text-xs opacity-60 col-span-2", style: { color: text } }, "Add 3\u20134 feature highlights to see them here."))), bottomStatement && /* @__PURE__ */ React.createElement("div", { className: "relative z-10 mt-6 bg-white rounded-xl px-5 py-3.5 text-center" }, /* @__PURE__ */ React.createElement("p", { className: "text-[13px] font-bold", style: { color: accent } }, bottomStatement)))
  );
};
const useAiThemeGenerator = () => {
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiThemeLoading, setAiThemeLoading] = useState(false);
  const [aiThemeError, setAiThemeError] = useState("");
  const [aiTheme, setAiTheme] = useState(null);
  const [themeHistory, setThemeHistory] = useState([]);
  const generate = async (contextData) => {
    const prompt = aiPrompt.trim();
    if (!prompt || aiThemeLoading) return;
    setAiThemeLoading(true);
    setAiThemeError("");
    try {
      const res = await fetch(AI_THEME_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, data: contextData || {} })
      });
      const resp = await res.json().catch(() => null);
      if (!res.ok || !resp?.theme) {
        throw new Error(resp?.detail || resp?.message || `Request failed (${res.status})`);
      }
      const newTheme = { ...resp.theme, id: `ai-${Date.now()}`, prompt };
      setAiTheme(newTheme);
      setThemeHistory((prev) => [newTheme, ...prev.filter((t) => t.id !== newTheme.id)].slice(0, 6));
    } catch (err) {
      setAiThemeError(
        `Couldn't generate a theme: ${err?.message || err}. Make sure the backend is running on ${GROWTHOS_API_BASE}.`
      );
    } finally {
      setAiThemeLoading(false);
    }
  };
  const reset = () => {
    setAiTheme(null);
    setAiThemeError("");
  };
  return { aiPrompt, setAiPrompt, aiThemeLoading, aiThemeError, aiTheme, setAiTheme, themeHistory, generate, reset };
};
const FeatureCardView = () => {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [form, setForm] = useState({
    productName: "",
    tagline: "",
    mobileNumber: "",
    email: "",
    website: "",
    address: "",
    bottomStatement: ""
  });
  const [features, setFeatures] = useState([emptyFeature(), emptyFeature(), emptyFeature()]);
  const [selectedThemeId, setSelectedThemeId] = useState(CARD_THEMES[0].id);
  const [selectedFontId, setSelectedFontId] = useState(FONT_OPTIONS[0].id);
  const [customFontName, setCustomFontName] = useState("");
  useGoogleFonts([...FONT_OPTIONS.map((f) => f.google), customFontName.trim() ? customFontName.trim().replace(/\s+/g, "+") : null]);
  const selectedFontStack = selectedFontId === "other" ? customFontName.trim() ? `'${customFontName.trim()}', sans-serif` : FONT_OPTIONS[0].stack : FONT_OPTIONS.find((f) => f.id === selectedFontId)?.stack || FONT_OPTIONS[0].stack;
  const [genError, setGenError] = useState("");
  const [downloadingPreview, setDownloadingPreview] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("");
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const previewRef = useRef(null);
  const [formScrollRef, formMaxHeight] = useAvailableHeight(24);
  const [previewPanelScrollRef, previewPanelMaxHeight] = useAvailableHeight(24);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [meRes, productsRes] = await Promise.allSettled([
        fetch(ME_ENDPOINT, { headers: authHeaders() }),
        fetch(PRODUCTS_ENDPOINT, { headers: authHeaders() })
      ]);
      const me = meRes.status === "fulfilled" && meRes.value.ok ? await meRes.value.json().catch(() => null) : null;
      const productsData = productsRes.status === "fulfilled" && productsRes.value.ok ? await productsRes.value.json().catch(() => null) : null;
      const firstProduct = productsData?.data?.[0];
      if (cancelled || (!me && !firstProduct)) return;
      setForm((prev) => ({
        ...prev,
        productName: prev.productName || firstProduct?.product_name || "",
        website: prev.website || me?.website || "",
        email: prev.email || me?.email || "",
        mobileNumber: prev.mobileNumber || me?.contact_number || "",
        address: prev.address || me?.company_location || ""
      }));
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const {
    aiPrompt,
    setAiPrompt,
    aiThemeLoading,
    aiThemeError,
    aiTheme,
    setAiTheme,
    themeHistory,
    generate: generateAiTheme,
    reset: handleResetTheme
  } = useAiThemeGenerator();
  const [savedThemes, setSavedThemes] = useState([]);
  const [savedThemesLoading, setSavedThemesLoading] = useState(false);
  const [saveThemeStatus, setSaveThemeStatus] = useState("");
  const [savingTheme, setSavingTheme] = useState(false);
  const isAiThemeActive = !!aiTheme;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSavedThemesLoading(true);
      try {
        const res = await fetch(SAVED_THEMES_ENDPOINT, { headers: authHeaders() });
        const resp = await res.json().catch(() => null);
        if (!cancelled && res.ok && Array.isArray(resp?.data)) {
          setSavedThemes(resp.data);
        }
      } catch {
      } finally {
        if (!cancelled) setSavedThemesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };
  const updateFeature = (index, key, value) => {
    setFeatures((prev) => prev.map((f, i) => i === index ? { ...f, [key]: value } : f));
  };
  const addFeature = () => {
    if (features.length >= MAX_FEATURES) return;
    setFeatures((prev) => [...prev, emptyFeature()]);
  };
  const removeFeature = (index) => {
    setFeatures((prev) => prev.filter((_, i) => i !== index));
  };
  const handleSelectManualTheme = (id) => {
    setSelectedThemeId(id);
    setAiTheme(null);
  };
  const handleGenerateAiTheme = () => generateAiTheme(form);
  const handleSaveTheme = async () => {
    if (!aiTheme || savingTheme) return;
    setSavingTheme(true);
    setSaveThemeStatus("");
    try {
      const res = await fetch(SAVED_THEMES_ENDPOINT, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ theme: aiTheme, prompt: aiTheme.prompt || "" })
      });
      const resp = await res.json().catch(() => null);
      if (!res.ok || !resp?.success) {
        throw new Error(resp?.detail || resp?.message || `Request failed (${res.status})`);
      }
      if (resp.already_saved) {
        setSaveThemeStatus("Already saved");
      } else {
        setSaveThemeStatus("Theme saved");
        if (resp.data) {
          setSavedThemes((prev) => [resp.data, ...prev]);
        }
      }
    } catch (err) {
      setSaveThemeStatus(`Couldn't save theme: ${err?.message || err}`);
    } finally {
      setSavingTheme(false);
    }
  };
  const filteredFeatures = features.filter((f) => f.title.trim());
  const contactRows = [
    { Icon: Phone, value: form.mobileNumber, placeholder: "+91 XXXXXXXXXX" },
    { Icon: Mail, value: form.email, placeholder: "you@company.com" },
    { Icon: Globe, value: form.website, placeholder: null },
    { Icon: MapPin, value: form.address, placeholder: null }
  ].filter((row) => row.value || row.placeholder);
  const ActiveTheme = CARD_THEMES.find((t) => t.id === selectedThemeId)?.Component || CARD_THEMES[0].Component;
  const handleDownloadPreview = async (fmt) => {
    if (!previewRef.current || downloadingPreview) return;
    setDownloadingPreview(true);
    setDownloadFormat(fmt);
    setDownloadMenuOpen(false);
    setGenError("");
    try {
      const { default: html2canvas } = await import("html2canvas-pro");
      const canvas = await html2canvas(previewRef.current, {
        backgroundColor: "#050505",
        scale: 2,
        useCORS: true,
        onclone: (doc) => {
          doc.querySelectorAll("[data-fit-inner]").forEach((el) => {
            el.style.transform = "none";
            el.style.marginLeft = "0";
          });
          doc.querySelectorAll("[data-fit-outer]").forEach((el) => {
            el.style.overflow = "visible";
            el.style.height = "auto";
            el.style.width = "760px";
          });
        }
      });
      const slug = form.productName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "feature-card";
      if (fmt === "pdf") {
        const { jsPDF } = await import("jspdf");
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: canvas.width >= canvas.height ? "landscape" : "portrait",
          unit: "px",
          format: [canvas.width, canvas.height]
        });
        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
        pdf.save(`${slug}-feature-card.pdf`);
      } else {
        const link = document.createElement("a");
        link.download = `${slug}-feature-card.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
      }
    } catch (err) {
      console.error("Preview export failed:", err);
      setGenError(
        `Couldn't export the preview: ${err?.message || err}. Make sure \`npm install html2canvas-pro jspdf\` has been run and the dev server was restarted.`
      );
    } finally {
      setDownloadingPreview(false);
      setDownloadFormat("");
    }
  };
  return /* @__PURE__ */ React.createElement("div", { className: "mt-6 lg:flex-1 lg:min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-6 pb-2" }, /* @__PURE__ */ React.createElement("div", { ref: formScrollRef, className: "thin-scrollbar space-y-5 lg:overflow-y-auto lg:pr-3", style: { maxHeight: formMaxHeight ? `${formMaxHeight}px` : void 0 } }, genError && /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2.5 text-[15px] text-red-400 border border-red-500/20 bg-red-500/[0.06] rounded-2xl px-4 py-3.5" }, /* @__PURE__ */ React.createElement(AlertCircle, { size: 16, className: "shrink-0" }), " ", genError), /* @__PURE__ */ React.createElement(SectionCard, { icon: ImageIcon, title: "Brand & Identity", subtitle: "Logo, name, and tagline" }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-4" }, /* @__PURE__ */ React.createElement("div", { className: "h-16 w-16 rounded-2xl border border-dashed border-orange-500/30 bg-white/[0.03] flex items-center justify-center overflow-hidden shrink-0" }, logoPreview ? /* @__PURE__ */ React.createElement(
    "img",
    {
      src: logoPreview,
      alt: "Logo preview",
      className: "h-full w-full object-cover object-center"
    }
  ) : /* @__PURE__ */ React.createElement(ImageIcon, { size: 20, className: "text-gray-600" })), /* @__PURE__ */ React.createElement("label", { className: "inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-semibold rounded-xl px-4 py-2.5 cursor-pointer transition" }, /* @__PURE__ */ React.createElement(Upload, { size: 13 }), logoFile ? "Change logo" : "Upload logo", /* @__PURE__ */ React.createElement("input", { type: "file", accept: "image/*", className: "hidden", onChange: handleLogoChange }))), /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-4" }, /* @__PURE__ */ React.createElement(Field, { label: "Product / Company Name", required: true }, /* @__PURE__ */ React.createElement(
    "input",
    {
      className: inputClass,
      placeholder: "e.g. GrowthOS AI",
      value: form.productName,
      onChange: setField("productName")
    }
  )), /* @__PURE__ */ React.createElement(Field, { label: "Tagline" }, /* @__PURE__ */ React.createElement(
    "input",
    {
      className: inputClass,
      placeholder: "e.g. Smart. Simple. AI-Powered.",
      value: form.tagline,
      onChange: setField("tagline")
    }
  )))), /* @__PURE__ */ React.createElement(SectionCard, { icon: Phone, title: "Contact Details", subtitle: "Shown on the left panel of the card" }, /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-4" }, /* @__PURE__ */ React.createElement(Field, { label: "Mobile Number", required: true }, /* @__PURE__ */ React.createElement(
    IconInput,
    {
      icon: Phone,
      placeholder: "+91 XXXXXXXXXX",
      value: form.mobileNumber,
      onChange: setField("mobileNumber")
    }
  )), /* @__PURE__ */ React.createElement(Field, { label: "Email", required: true }, /* @__PURE__ */ React.createElement(
    IconInput,
    {
      icon: Mail,
      type: "email",
      placeholder: "you@company.com",
      value: form.email,
      onChange: setField("email")
    }
  ))), /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-4" }, /* @__PURE__ */ React.createElement(Field, { label: "Website" }, /* @__PURE__ */ React.createElement(
    IconInput,
    {
      icon: Globe,
      placeholder: "https://yourproduct.com",
      value: form.website,
      onChange: setField("website")
    }
  )), /* @__PURE__ */ React.createElement(Field, { label: "Address" }, /* @__PURE__ */ React.createElement(
    IconInput,
    {
      icon: MapPin,
      placeholder: "City, State",
      value: form.address,
      onChange: setField("address")
    }
  )))), /* @__PURE__ */ React.createElement(
    SectionCard,
    {
      icon: Sparkles,
      title: "Feature Highlights",
      subtitle: `${features.filter((f) => f.title.trim()).length} of ${MAX_FEATURES} added \u2014 shown as a grid on the card`,
      action: features.length < MAX_FEATURES && /* @__PURE__ */ React.createElement(
        "button",
        {
          type: "button",
          onClick: addFeature,
          className: "text-sm font-semibold text-orange-400 hover:text-orange-300 transition shrink-0"
        },
        "+ Add feature"
      )
    },
    features.map((f, i) => /* @__PURE__ */ React.createElement(
      "div",
      {
        key: i,
        className: "group relative rounded-xl bg-white/[0.03] hover:bg-white/[0.05] border border-white/5 p-4 pl-12 transition"
      },
      /* @__PURE__ */ React.createElement("div", { className: "absolute left-4 top-4 h-6 w-6 rounded-full bg-orange-500/15 border border-orange-500/30 flex items-center justify-center text-xs font-bold text-orange-400" }, i + 1),
      /* @__PURE__ */ React.createElement("div", { className: "space-y-2" }, /* @__PURE__ */ React.createElement(
        "input",
        {
          className: inputClass,
          placeholder: `Feature ${i + 1} title (e.g. AI-Powered Learning)`,
          value: f.title,
          onChange: (e) => updateFeature(i, "title", e.target.value)
        }
      ), /* @__PURE__ */ React.createElement(
        "input",
        {
          className: inputClass,
          placeholder: "Short description",
          value: f.description,
          onChange: (e) => updateFeature(i, "description", e.target.value)
        }
      )),
      features.length > 1 && /* @__PURE__ */ React.createElement(
        "button",
        {
          type: "button",
          onClick: () => removeFeature(i),
          className: "absolute right-3.5 top-3.5 text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition",
          "aria-label": "Remove feature"
        },
        /* @__PURE__ */ React.createElement(X, { size: 15 })
      )
    ))
  ), /* @__PURE__ */ React.createElement(SectionCard, { icon: FileText, title: "Closing Statement", subtitle: "Shown in the banner at the bottom" }, /* @__PURE__ */ React.createElement(
    "input",
    {
      className: inputClass,
      placeholder: "e.g. Empowering Institutions. Enabling Futures.",
      value: form.bottomStatement,
      onChange: setField("bottomStatement")
    }
  ))), /* @__PURE__ */ React.createElement("div", { ref: previewPanelScrollRef, className: "thin-scrollbar space-y-3 lg:overflow-y-auto lg:pr-3", style: { maxHeight: previewPanelMaxHeight ? `${previewPanelMaxHeight}px` : void 0 } }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center justify-between mb-2" }, /* @__PURE__ */ React.createElement("p", { className: "text-sm font-semibold text-gray-400 tracking-wide uppercase" }, "Preview"), /* @__PURE__ */ React.createElement("div", { className: "relative" }, /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: () => setDownloadMenuOpen((v) => !v),
      disabled: downloadingPreview,
      className: "inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-[15px] font-semibold rounded-xl px-5 py-3 transition"
    },
    downloadingPreview ? /* @__PURE__ */ React.createElement(Loader2, { size: 16, className: "animate-spin" }) : /* @__PURE__ */ React.createElement(Download, { size: 16 }),
    downloadingPreview ? `Exporting ${downloadFormat.toUpperCase()}...` : "Download",
    !downloadingPreview && /* @__PURE__ */ React.createElement(ChevronDown, { size: 16 })
  ), downloadMenuOpen && !downloadingPreview && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "fixed inset-0 z-10", onClick: () => setDownloadMenuOpen(false) }), /* @__PURE__ */ React.createElement("div", { className: "absolute right-0 mt-2 w-40 bg-black border border-orange-600/30 rounded-xl overflow-hidden shadow-xl z-20" }, /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: () => handleDownloadPreview("png"),
      className: "w-full text-left px-4 py-3 text-[15px] font-semibold text-white hover:bg-orange-500/10 transition"
    },
    "PNG Image"
  ), /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: () => handleDownloadPreview("pdf"),
      className: "w-full text-left px-4 py-3 text-[15px] font-semibold text-white hover:bg-orange-500/10 transition border-t border-orange-600/10"
    },
    "PDF Document"
  ))))), /* @__PURE__ */ React.createElement("div", { className: "rounded-2xl border border-orange-600/20 bg-black p-2" }, /* @__PURE__ */ React.createElement(FitCard, { maxHeight: previewPanelMaxHeight }, /* @__PURE__ */ React.createElement("div", { ref: previewRef, className: "rounded-xl overflow-hidden", style: { fontFamily: selectedFontStack } }, /* @__PURE__ */ React.createElement(AnimatePresence, { mode: "wait" }, isAiThemeActive ? /* @__PURE__ */ React.createElement(
    motion.div,
    {
      key: aiTheme.id,
      initial: { opacity: 0, y: 8 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -8 },
      transition: { duration: 0.35, ease: "easeOut" }
    },
    /* @__PURE__ */ React.createElement(
      DynamicThemeCard,
      {
        theme: { ...aiTheme, font: selectedFontStack },
        logoPreview,
        productName: form.productName,
        tagline: form.tagline,
        contactRows,
        features: filteredFeatures,
        bottomStatement: form.bottomStatement
      }
    )
  ) : /* @__PURE__ */ React.createElement(
    motion.div,
    {
      key: selectedThemeId,
      initial: { opacity: 0, y: 8 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -8 },
      transition: { duration: 0.35, ease: "easeOut" }
    },
    /* @__PURE__ */ React.createElement(
      ActiveTheme,
      {
        logoPreview,
        productName: form.productName,
        tagline: form.tagline,
        contactRows,
        features: filteredFeatures,
        bottomStatement: form.bottomStatement
      }
    )
  ))))), /* @__PURE__ */ React.createElement("div", { className: "mt-6" }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2 mb-3" }, /* @__PURE__ */ React.createElement(Palette, { size: 14, className: "text-orange-500" }), /* @__PURE__ */ React.createElement("p", { className: "text-sm font-semibold text-gray-400 tracking-wide uppercase" }, "Choose a design")), /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-3" }, CARD_THEMES.map((t) => {
    const isActive = !isAiThemeActive && selectedThemeId === t.id;
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        key: t.id,
        type: "button",
        onClick: () => handleSelectManualTheme(t.id),
        className: `group relative text-left rounded-xl border p-3.5 transition ${isActive ? "border-orange-500 bg-orange-500/[0.06]" : "border-white/10 bg-white/[0.02] hover:border-orange-500/40 hover:bg-white/[0.04]"}`
      },
      /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2.5" }, /* @__PURE__ */ React.createElement(
        "div",
        {
          className: "h-8 w-8 rounded-lg border border-white/15 shrink-0",
          style: { background: t.swatch }
        }
      ), /* @__PURE__ */ React.createElement("div", { className: "min-w-0" }, /* @__PURE__ */ React.createElement("p", { className: "text-white text-sm font-semibold leading-tight truncate" }, t.label), /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 text-xs mt-0.5" }, isActive ? "Applied to preview" : "Tap to preview")), isActive && /* @__PURE__ */ React.createElement(Check, { size: 14, className: "text-orange-500 ml-auto shrink-0" }))
    );
  }))), /* @__PURE__ */ React.createElement("div", { className: "mt-6" }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2 mb-3" }, /* @__PURE__ */ React.createElement(FileText, { size: 14, className: "text-orange-500" }), /* @__PURE__ */ React.createElement("p", { className: "text-sm font-semibold text-gray-400 tracking-wide uppercase" }, "Font Style")), /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-2 sm:grid-cols-4 gap-2.5" }, FONT_OPTIONS.map((f) => {
    const isActive = selectedFontId === f.id;
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        key: f.id,
        type: "button",
        onClick: () => setSelectedFontId(f.id),
        className: `text-left rounded-lg border px-3 py-2 transition ${isActive ? "border-orange-500/60 bg-orange-500/[0.08]" : "border-white/10 bg-white/[0.02] hover:border-orange-500/40"}`,
        style: f.stack ? { fontFamily: f.stack } : void 0
      },
      /* @__PURE__ */ React.createElement("span", { className: `text-[13px] ${isActive ? "text-orange-300" : "text-gray-200"}` }, f.label)
    );
  })), selectedFontId === "other" && /* @__PURE__ */ React.createElement(
    "input",
    {
      className: `${inputClass} mt-2.5`,
      placeholder: "Type any font name, e.g. Nunito, Georgia, Roboto Slab...",
      value: customFontName,
      onChange: (e) => setCustomFontName(e.target.value),
      style: { fontFamily: selectedFontStack }
    }
  )), /* @__PURE__ */ React.createElement("div", { className: "mt-6 rounded-2xl border border-white/5 bg-white/[0.02] p-5" }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2.5" }, /* @__PURE__ */ React.createElement("div", { className: "h-8 w-8 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0" }, /* @__PURE__ */ React.createElement(Wand2, { size: 15, className: "text-orange-500" })), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h4", { className: "text-white text-[15px] font-semibold leading-tight" }, "AI Theme Generator"), /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 text-sm mt-0.5" }, "Describe a look and apply it to the preview instantly \u2014 your content never changes."))), /* @__PURE__ */ React.createElement(
    "textarea",
    {
      value: aiPrompt,
      onChange: (e) => setAiPrompt(e.target.value),
      rows: 3,
      placeholder: "Describe your design...\n\nExample: Create a premium Apple-style card with glassmorphism, rounded corners, blue gradients and elegant typography.",
      className: `${inputClass} mt-4 resize-none`
    }
  ), aiThemeError && /* @__PURE__ */ React.createElement("div", { className: "mt-3 flex items-center gap-2 text-sm text-red-400 border border-red-500/20 bg-red-500/[0.06] rounded-xl px-3.5 py-2.5" }, /* @__PURE__ */ React.createElement(AlertCircle, { size: 13, className: "shrink-0" }), " ", aiThemeError), /* @__PURE__ */ React.createElement("div", { className: "mt-4 flex flex-wrap items-center gap-2.5" }, /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: handleGenerateAiTheme,
      disabled: !aiPrompt.trim() || aiThemeLoading,
      className: "inline-flex items-center gap-2 bg-orange-500/15 hover:bg-orange-500/25 backdrop-blur-sm border border-orange-400/30 disabled:opacity-40 disabled:cursor-not-allowed text-orange-200 text-xs font-semibold rounded-lg px-3.5 py-2 transition"
    },
    aiThemeLoading ? /* @__PURE__ */ React.createElement(Loader2, { size: 13, className: "animate-spin" }) : /* @__PURE__ */ React.createElement(Wand2, { size: 13 }),
    aiThemeLoading ? "Generating..." : "Generate Theme"
  ), isAiThemeActive && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: handleSaveTheme,
      disabled: savingTheme,
      className: "inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed border border-white/10 text-white text-sm font-semibold rounded-xl px-3.5 py-2.5 transition"
    },
    savingTheme ? /* @__PURE__ */ React.createElement(Loader2, { size: 13, className: "animate-spin" }) : /* @__PURE__ */ React.createElement(Save, { size: 13 }),
    savingTheme ? "Saving..." : "Save Theme"
  ), /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: handleResetTheme,
      className: "inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 text-sm font-semibold rounded-xl px-3.5 py-2.5 transition"
    },
    /* @__PURE__ */ React.createElement(RotateCcw, { size: 13 }),
    " Reset to Default"
  ))), isAiThemeActive && /* @__PURE__ */ React.createElement("div", { className: "mt-4 flex items-center gap-2 text-xs text-orange-400 bg-orange-500/[0.06] border border-orange-500/20 rounded-lg px-3 py-2" }, /* @__PURE__ */ React.createElement(Check, { size: 13, className: "shrink-0" }), 'AI theme "', aiTheme.themeName || "Custom", '" is applied to the preview.'), saveThemeStatus && /* @__PURE__ */ React.createElement(
    "div",
    {
      className: `mt-2 flex items-center gap-2 text-xs rounded-lg px-3 py-2 border ${saveThemeStatus.startsWith("Couldn't") ? "text-red-400 bg-red-500/[0.06] border-red-500/20" : "text-emerald-400 bg-emerald-500/[0.06] border-emerald-500/20"}`
    },
    saveThemeStatus.startsWith("Couldn't") ? /* @__PURE__ */ React.createElement(AlertCircle, { size: 13, className: "shrink-0" }) : /* @__PURE__ */ React.createElement(Check, { size: 13, className: "shrink-0" }),
    saveThemeStatus
  ), themeHistory.length > 0 && /* @__PURE__ */ React.createElement("div", { className: "mt-5" }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-1.5 mb-2" }, /* @__PURE__ */ React.createElement(History, { size: 12, className: "text-gray-500" }), /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-semibold text-gray-500 tracking-wide uppercase" }, "Theme History")), /* @__PURE__ */ React.createElement("div", { className: "flex flex-wrap gap-2" }, themeHistory.map((t, i) => /* @__PURE__ */ React.createElement(
    "button",
    {
      key: t.id,
      type: "button",
      onClick: () => setAiTheme(t),
      title: t.prompt,
      className: `flex items-center gap-2 rounded-lg border px-2.5 py-1.5 transition ${aiTheme?.id === t.id ? "border-orange-500 bg-orange-500/[0.08]" : "border-white/10 bg-white/[0.02] hover:border-orange-500/30"}`
    },
    /* @__PURE__ */ React.createElement(
      "span",
      {
        className: "h-4 w-4 rounded-full border border-white/20 shrink-0",
        style: { background: t.background }
      }
    ),
    /* @__PURE__ */ React.createElement("span", { className: "text-xs text-gray-300 font-medium" }, "Theme ", themeHistory.length - i)
  )))), savedThemesLoading && /* @__PURE__ */ React.createElement("div", { className: "mt-5 flex items-center gap-2 text-xs text-gray-500" }, /* @__PURE__ */ React.createElement(Loader2, { size: 12, className: "animate-spin" }), " Loading saved themes..."), !savedThemesLoading && savedThemes.length > 0 && /* @__PURE__ */ React.createElement("div", { className: "mt-5" }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-1.5 mb-2" }, /* @__PURE__ */ React.createElement(Save, { size: 12, className: "text-gray-500" }), /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-semibold text-gray-500 tracking-wide uppercase" }, "Saved Themes")), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col gap-1.5" }, savedThemes.map((entry) => /* @__PURE__ */ React.createElement(
    "button",
    {
      key: entry.id,
      type: "button",
      onClick: () => setAiTheme(entry.theme),
      className: "flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.02] hover:border-orange-500/30 px-3 py-2 text-left transition"
    },
    /* @__PURE__ */ React.createElement(
      "span",
      {
        className: "h-5 w-5 rounded-full border border-white/20 shrink-0",
        style: { background: entry.theme.background }
      }
    ),
    /* @__PURE__ */ React.createElement("span", { className: "text-xs text-gray-300 truncate flex-1" }, entry.theme.themeName || entry.prompt)
  )))))));
};
const FLYER_MAX_ITEMS = 8;
const MAX_SCREENSHOTS = 8;
const MAX_TESTIMONIALS = 6;
const MAX_PRICING_PLANS = 4;
const FLYER_LAYOUTS = [
  { id: "onePage", label: "One Page", icon: FileText, desc: "Always exactly one A4 page \u2014 content auto-fits, nothing spills or gets cut." },
  { id: "twoPage", label: "Two Page", icon: Columns, desc: "Cover page + one content page \u2014 always exactly two A4 pages." }
];
const FLYER_FORM_TABS = [
  { id: "brand", label: "Brand Information", subtitle: "Your company and brand details", icon: ImageIcon },
  { id: "product", label: "Product Details", subtitle: "What you're showcasing", icon: Sparkles },
  { id: "problem", label: "Problem & Solution", subtitle: "Why customers need this", icon: FileText },
  { id: "features", label: "Features", subtitle: "Highlight up to 8 features", icon: Sparkles },
  { id: "benefits", label: "Benefits", subtitle: "The outcomes customers get", icon: Check },
  { id: "why", label: "Why Choose Us", subtitle: "What sets you apart", icon: ArrowUpRight },
  { id: "media", label: "Media & Screenshots", subtitle: "Upload up to 8 product images", icon: ImagePlus },
  { id: "pricing", label: "Pricing Plans", subtitle: "Optional \u2014 leave off to skip this page", icon: DollarSign },
  { id: "testimonials", label: "Testimonials", subtitle: "Social proof from real customers", icon: Quote },
  { id: "contact", label: "Contact Information", subtitle: "How prospects reach you", icon: Phone },
  { id: "cta", label: "CTA Section", subtitle: "What should the reader do next?", icon: ArrowUpRight },
  { id: "theme", label: "Theme", subtitle: "Colors and AI styling", icon: Palette }
];
const FLYER_TEMPLATES = [
  { id: "classic", label: "Classic", desc: "The original clean, section-by-section layout." },
  { id: "modern", label: "Modern", desc: "Hero banner, icon-badge feature cards, icon contact row." }
];
const DEFAULT_FLYER_THEME = {
  themeName: "Default",
  background: "linear-gradient(135deg, #111111 0%, #1a1a1a 100%)",
  accent: "#f97316",
  text: "#ffffff",
  font: "Inter, sans-serif",
  borderRadius: 16
};
const emptyPricingPlan = () => ({ name: "", price: "", period: "", featuresText: "", highlighted: false });
const emptyTestimonial = () => ({ quote: "", author: "", role: "" });
const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});
const updateListItem = (setter) => (index, key, value) => setter((prev) => prev.map((item, i) => i === index ? { ...item, [key]: value } : item));
const addListItem = (setter, max, factory) => () => setter((prev) => prev.length >= max ? prev : [...prev, factory()]);
const removeListItem = (setter) => (index) => setter((prev) => prev.filter((_, i) => i !== index));
const TitleDescListEditor = ({ items, onUpdate, onAdd, onRemove, max, itemLabel }) => /* @__PURE__ */ React.createElement("div", { className: "space-y-3" }, items.map((item, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex gap-2 items-start bg-white/[0.02] border border-white/5 rounded-xl p-3" }, /* @__PURE__ */ React.createElement("div", { className: "flex-1 space-y-2" }, /* @__PURE__ */ React.createElement(
  "input",
  {
    className: inputClass,
    placeholder: `${itemLabel} title`,
    value: item.title,
    onChange: (e) => onUpdate(i, "title", e.target.value)
  }
), /* @__PURE__ */ React.createElement(
  "input",
  {
    className: inputClass,
    placeholder: "Short description",
    value: item.description,
    onChange: (e) => onUpdate(i, "description", e.target.value)
  }
)), /* @__PURE__ */ React.createElement(
  "button",
  {
    type: "button",
    onClick: () => onRemove(i),
    className: "mt-1 h-8 w-8 flex items-center justify-center rounded-lg border border-white/10 text-gray-500 hover:text-red-400 hover:border-red-500/30 transition shrink-0"
  },
  /* @__PURE__ */ React.createElement(Trash2, { size: 14 })
))), items.length < max && /* @__PURE__ */ React.createElement(
  "button",
  {
    type: "button",
    onClick: onAdd,
    className: "inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400 hover:text-orange-300 transition"
  },
  /* @__PURE__ */ React.createElement(Plus, { size: 14 }),
  " Add ",
  itemLabel.toLowerCase()
));
const ScreenshotUploader = ({ screenshots, onAddFiles, onRemove, onCaptionChange, max }) => /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-3 sm:grid-cols-4 gap-3" }, screenshots.map((s, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "relative group rounded-xl overflow-hidden border border-white/10 bg-white/[0.02]" }, /* @__PURE__ */ React.createElement("img", { src: s.dataUrl, alt: "", className: "h-20 w-full object-cover" }), /* @__PURE__ */ React.createElement(
  "button",
  {
    type: "button",
    onClick: () => onRemove(i),
    className: "absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition"
  },
  /* @__PURE__ */ React.createElement(X, { size: 12 })
), /* @__PURE__ */ React.createElement(
  "input",
  {
    value: s.caption,
    onChange: (e) => onCaptionChange(i, e.target.value),
    placeholder: "Caption",
    className: "w-full bg-black/40 text-white text-[10px] px-2 py-1 outline-none placeholder:text-gray-500"
  }
))), screenshots.length < max && /* @__PURE__ */ React.createElement("label", { className: "h-full min-h-[72px] rounded-xl border border-dashed border-orange-500/30 flex flex-col items-center justify-center gap-1 cursor-pointer text-gray-500 hover:text-orange-400 hover:border-orange-400/50 transition" }, /* @__PURE__ */ React.createElement(ImagePlus, { size: 16 }), /* @__PURE__ */ React.createElement("span", { className: "text-[10px] font-medium" }, "Add images"), /* @__PURE__ */ React.createElement(
  "input",
  {
    type: "file",
    accept: "image/*",
    multiple: true,
    className: "hidden",
    onChange: (e) => onAddFiles(e.target.files)
  }
)));
const PricingPlansEditor = ({ plans, onUpdate, onAdd, onRemove, max }) => /* @__PURE__ */ React.createElement("div", { className: "space-y-3" }, plans.map((plan, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "bg-white/[0.02] border border-white/5 rounded-xl p-3 space-y-2" }, /* @__PURE__ */ React.createElement("div", { className: "flex gap-2" }, /* @__PURE__ */ React.createElement("input", { className: inputClass, placeholder: "Plan name", value: plan.name, onChange: (e) => onUpdate(i, "name", e.target.value) }), /* @__PURE__ */ React.createElement("input", { className: inputClass, placeholder: "Price (e.g. $49)", value: plan.price, onChange: (e) => onUpdate(i, "price", e.target.value) }), /* @__PURE__ */ React.createElement("input", { className: inputClass, placeholder: "/ month", value: plan.period, onChange: (e) => onUpdate(i, "period", e.target.value) })), /* @__PURE__ */ React.createElement(
  "input",
  {
    className: inputClass,
    placeholder: "Plan features, comma separated",
    value: plan.featuresText,
    onChange: (e) => onUpdate(i, "featuresText", e.target.value)
  }
), /* @__PURE__ */ React.createElement("div", { className: "flex items-center justify-between" }, /* @__PURE__ */ React.createElement("label", { className: "inline-flex items-center gap-2 text-xs text-gray-400" }, /* @__PURE__ */ React.createElement("input", { type: "checkbox", checked: plan.highlighted, onChange: (e) => onUpdate(i, "highlighted", e.target.checked) }), "Highlight as most popular"), /* @__PURE__ */ React.createElement("button", { type: "button", onClick: () => onRemove(i), className: "text-gray-500 hover:text-red-400 transition" }, /* @__PURE__ */ React.createElement(Trash2, { size: 14 }))))), plans.length < max && /* @__PURE__ */ React.createElement("button", { type: "button", onClick: onAdd, className: "inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400 hover:text-orange-300 transition" }, /* @__PURE__ */ React.createElement(Plus, { size: 14 }), " Add pricing plan"));
const TestimonialsEditor = ({ items, onUpdate, onAdd, onRemove, max }) => /* @__PURE__ */ React.createElement("div", { className: "space-y-3" }, items.map((t, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "bg-white/[0.02] border border-white/5 rounded-xl p-3 space-y-2" }, /* @__PURE__ */ React.createElement(
  "textarea",
  {
    className: `${inputClass} resize-none`,
    rows: 2,
    placeholder: "Customer quote",
    value: t.quote,
    onChange: (e) => onUpdate(i, "quote", e.target.value)
  }
), /* @__PURE__ */ React.createElement("div", { className: "flex gap-2" }, /* @__PURE__ */ React.createElement("input", { className: inputClass, placeholder: "Author name", value: t.author, onChange: (e) => onUpdate(i, "author", e.target.value) }), /* @__PURE__ */ React.createElement("input", { className: inputClass, placeholder: "Role / company", value: t.role, onChange: (e) => onUpdate(i, "role", e.target.value) }), /* @__PURE__ */ React.createElement("button", { type: "button", onClick: () => onRemove(i), className: "shrink-0 h-9 w-9 flex items-center justify-center rounded-lg border border-white/10 text-gray-500 hover:text-red-400 hover:border-red-500/30 transition" }, /* @__PURE__ */ React.createElement(Trash2, { size: 14 }))))), items.length < max && /* @__PURE__ */ React.createElement("button", { type: "button", onClick: onAdd, className: "inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400 hover:text-orange-300 transition" }, /* @__PURE__ */ React.createElement(Plus, { size: 14 }), " Add testimonial"));
const PX_PER_MM = 2.6;
const mm = (v) => `${(v * PX_PER_MM).toFixed(2)}px`;
const A4 = { w: 210, h: 297 };
const PAGE_MARGIN = { y: 16, x: 14 };
const INNER_H_PX = (A4.h - PAGE_MARGIN.y * 2) * PX_PER_MM;
const INNER_H_LANDSCAPE_PX = (A4.w - PAGE_MARGIN.y * 2) * PX_PER_MM;
const A4Page = ({ landscape = false, dense = false, accent = "#f97316", footer = "", fullBleed = false, background = "#ffffff", topBleed = null, bottomBleed = null, children, className = "", style = {} }) => {
  const w = landscape ? A4.h : A4.w;
  const h = landscape ? A4.w : A4.h;
  const pad = fullBleed ? "0" : `${mm(PAGE_MARGIN.y)} ${mm(PAGE_MARGIN.x)}`;
  return /* @__PURE__ */ React.createElement("div", { className: "flyer-page-frame" }, /* @__PURE__ */ React.createElement(
    "div",
    {
      className: `flyer-page ${className}`,
      "data-landscape": landscape ? "true" : "false",
      style: {
        width: mm(w),
        minHeight: mm(h),
        padding: 0,
        background: background || "#ffffff",
        ...style
      }
    },
    topBleed,
    /* @__PURE__ */ React.createElement("div", { className: `flyer-page-inner${dense ? " flyer-page-dense" : ""}`, style: { padding: pad } }, children),
    bottomBleed,
    footer && !fullBleed && /* @__PURE__ */ React.createElement("div", { className: "flyer-page-footer", style: { borderTopColor: `${accent}33`, padding: `0 ${mm(PAGE_MARGIN.x)} ${mm(PAGE_MARGIN.y)}` } }, /* @__PURE__ */ React.createElement("span", null, footer))
  ));
};
const getFilteredData = (raw) => {
  const features = (raw.features || []).filter((f) => f.title.trim());
  const benefits = (raw.benefits || []).filter((f) => f.title.trim());
  const why = (raw.whyChooseUs || []).filter((f) => f.title.trim());
  const testimonials = (raw.testimonials || []).filter((t) => t.quote.trim());
  const plans = (raw.pricingPlans || []).filter((p) => p.name.trim());
  const screenshots = (raw.screenshots || []).slice(0, 8);
  const hasContact = raw.contact && (raw.contact.phone || raw.contact.email || raw.contact.website || raw.contact.address);
  const hasCta = raw.cta && (raw.cta.heading || raw.cta.subtext);
  return { features, benefits, why, testimonials, plans, screenshots, hasContact, hasCta };
};
const estimateTextLines = (text, charsPerLine) => Math.max(1, Math.ceil((text || "").length / Math.max(1, charsPerLine)));
const est = {
  header: (dense) => dense ? 65 : 85,
  // Compact hero banner (company + tagline + product badge) that now
  // sits at the top of content page 1 instead of eating a whole page.
  coverCompact: (dense) => dense ? 92 : 112,
  heading: (dense) => dense ? 18 : 22,
  overview: (text, dense) => {
    const lines = estimateTextLines(text, dense ? 100 : 80);
    return 18 + lines * (dense ? 14 : 17);
  },
  problemSolution: (p, s, dense) => {
    const lp = estimateTextLines(p, dense ? 100 : 80);
    const ls = estimateTextLines(s, dense ? 100 : 80);
    return 18 + lp * (dense ? 14 : 17) + (s ? 8 : 0) + ls * (dense ? 14 : 17);
  },
  gridSection: (items, dense) => {
    if (!items || !items.length) return 0;
    const cols = 2;
    const rows = Math.ceil(items.length / cols);
    const charsPerLine = dense ? 30 : 34;
    const padding = dense ? 20 : 22;
    const titleH = dense ? 14 : 15;
    const descLineH = dense ? 12 : 13;
    let total = 0;
    for (let r = 0; r < rows; r++) {
      const rowItems = items.slice(r * cols, r * cols + cols);
      const rowH = Math.max(...rowItems.map((it) => {
        const descLines = it.description ? estimateTextLines(it.description, charsPerLine) : 0;
        return padding + titleH + (descLines ? 2 + descLines * descLineH : 0);
      }));
      total += rowH;
    }
    const buffer = Math.ceil(total * 0.08);
    return 18 + total + buffer + (rows - 1) * (dense ? 6 : 10);
  },
  screenshots: (count, dense) => {
    if (!count) return 0;
    const cols = dense ? 3 : 2;
    const rows = Math.ceil(count / cols);
    const imgH = dense ? 52 : 78;
    return 18 + rows * imgH + (rows - 1) * (dense ? 6 : 10);
  },
  pricing: (count, dense) => {
    if (!count) return 0;
    const planH = dense ? 72 : 96;
    return 18 + count * planH + (count - 1) * (dense ? 6 : 10);
  },
  testimonials: (count, dense) => {
    if (!count) return 0;
    const itemH = dense ? 44 : 60;
    return 18 + count * itemH + (count - 1) * (dense ? 6 : 10);
  },
  contact: (dense) => dense ? 58 : 76,
  cta: (dense) => dense ? 78 : 108,
  gap: (dense) => dense ? 10 : 16
};
const buildSectionMetas = (data, filtered) => {
  const dense = false;
  const list = [];
  const push = (id, render, height, priority = 5) => list.push({ id, render, height: Math.round(height), priority });
  if (data.product?.productOverview?.trim()) {
    push("overview", "overview", est.overview(data.product.productOverview, dense) + est.gap(dense));
  }
  if (data.problemStatement?.trim() || data.solutionStatement?.trim()) {
    push("problemSolution", "problemSolution", est.problemSolution(data.problemStatement, data.solutionStatement, dense) + est.gap(dense));
  }
  if (filtered.features.length) {
    push("features", "features", est.gridSection(filtered.features, dense) + est.gap(dense), 4);
  }
  if (filtered.benefits.length) {
    push("benefits", "benefits", est.gridSection(filtered.benefits, dense) + est.gap(dense), 4);
  }
  if (filtered.why.length) {
    push("whyChooseUs", "whyChooseUs", est.gridSection(filtered.why, dense) + est.gap(dense), 4);
  }
  if (filtered.screenshots.length) {
    push("screenshots", "screenshots", est.screenshots(filtered.screenshots.length, dense) + est.gap(dense), 3);
  }
  const pricingH = data.pricingEnabled && filtered.plans.length ? est.pricing(filtered.plans.length, dense) + est.gap(dense) : 0;
  const testimonialsH = filtered.testimonials.length ? est.testimonials(filtered.testimonials.length, dense) + est.gap(dense) : 0;
  if (pricingH && testimonialsH) {
    push("pricingTestimonials", "pricingTestimonials", pricingH + testimonialsH, 3);
  } else if (pricingH) {
    push("pricing", "pricing", pricingH, 3);
  } else if (testimonialsH) {
    push("testimonials", "testimonials", testimonialsH, 3);
  }
  return list;
};
const SectionHeading = ({ children, accent }) => /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-extrabold uppercase tracking-[0.12em] mb-2", style: { color: accent } }, children);
const FeatureGrid = ({ items, dense = false }) => /* @__PURE__ */ React.createElement("div", { className: `grid ${dense ? "grid-cols-2 gap-2" : "grid-cols-2 gap-2.5"}` }, items.map((it, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "border border-gray-200 rounded-lg p-2.5" }, /* @__PURE__ */ React.createElement("p", { className: "text-[11.5px] font-bold text-gray-900 leading-tight" }, it.title), it.description && /* @__PURE__ */ React.createElement("p", { className: "text-[10px] text-gray-500 mt-0.5 leading-snug" }, it.description))));
const ScreenshotGallery = ({ items, maxCols = 2, dense = false }) => /* @__PURE__ */ React.createElement("div", { className: `grid ${maxCols === 3 ? "grid-cols-3" : "grid-cols-2"} ${dense ? "gap-1.5" : "gap-2"}` }, items.map((s, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "rounded-lg border border-gray-200 overflow-hidden" }, /* @__PURE__ */ React.createElement("img", { src: s.dataUrl, alt: "", className: `w-full object-cover ${dense ? "h-14" : "h-20"}` }), s.caption && /* @__PURE__ */ React.createElement("p", { className: "text-[9px] text-gray-500 px-1.5 py-1 truncate" }, s.caption))));
const PricingBlock = ({ plans, accent, dense = false }) => /* @__PURE__ */ React.createElement("div", { className: `flex ${dense ? "gap-1.5" : "gap-2"}` }, plans.map((p, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: `flex-1 rounded-lg ${dense ? "p-2" : "p-2.5"}`, style: { border: p.highlighted ? `2px solid ${accent}` : "1px solid #e5e7eb" } }, /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-bold text-gray-900" }, p.name), /* @__PURE__ */ React.createElement("p", { className: "text-[13px] font-extrabold", style: { color: accent } }, p.price, /* @__PURE__ */ React.createElement("span", { className: "text-[9px] font-medium text-gray-400 ml-1" }, p.period)), p.featuresText && /* @__PURE__ */ React.createElement("p", { className: "text-[9px] text-gray-500 mt-1 leading-snug" }, p.featuresText))));
const TestimonialsBlock = ({ items, accent }) => /* @__PURE__ */ React.createElement("div", { className: "space-y-2" }, items.map((t, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "border-l-2 pl-2.5", style: { borderColor: accent } }, /* @__PURE__ */ React.createElement("p", { className: "text-[10.5px] italic text-gray-600 leading-snug" }, "\u201C", t.quote, "\u201D"), /* @__PURE__ */ React.createElement("p", { className: "text-[10px] font-semibold text-gray-800 mt-0.5" }, t.author, t.role ? ` \u2014 ${t.role}` : ""))));
const ContactBlock = ({ contact }) => /* @__PURE__ */ React.createElement("div", { className: "flex flex-col gap-1 text-[10.5px] text-gray-600" }, contact.phone && /* @__PURE__ */ React.createElement("span", { className: "flex items-start gap-1.5 min-w-0 [overflow-wrap:anywhere]" }, /* @__PURE__ */ React.createElement(Phone, { size: 10, className: "shrink-0 mt-[3px]" }), " ", contact.phone), contact.email && /* @__PURE__ */ React.createElement("span", { className: "flex items-start gap-1.5 min-w-0 [overflow-wrap:anywhere]" }, /* @__PURE__ */ React.createElement(Mail, { size: 10, className: "shrink-0 mt-[3px]" }), " ", contact.email), contact.website && /* @__PURE__ */ React.createElement("span", { className: "flex items-start gap-1.5 min-w-0 [overflow-wrap:anywhere]" }, /* @__PURE__ */ React.createElement(Globe, { size: 10, className: "shrink-0 mt-[3px]" }), " ", contact.website), contact.address && /* @__PURE__ */ React.createElement("span", { className: "flex items-start gap-1.5 min-w-0 [overflow-wrap:anywhere]" }, /* @__PURE__ */ React.createElement(MapPin, { size: 10, className: "shrink-0 mt-[3px]" }), " ", contact.address));
const CTABlock = ({ cta, theme, compact = false, squared = false }) => {
  if (!cta.heading && !cta.subtext) return null;
  return /* @__PURE__ */ React.createElement("div", { className: `${squared ? "" : "rounded-xl"} text-center text-white mt-auto ${compact ? "p-3" : "p-4"}`, style: { background: theme.background } }, /* @__PURE__ */ React.createElement("p", { className: `font-extrabold ${compact ? "text-[12px]" : "text-[13px]"}` }, cta.heading || "Ready to get started?"), cta.subtext && /* @__PURE__ */ React.createElement("p", { className: `opacity-80 mt-1 ${compact ? "text-[10px]" : "text-[10.5px]"}` }, cta.subtext), /* @__PURE__ */ React.createElement("span", { className: "inline-block mt-2 px-4 py-1.5 rounded-full text-[10.5px] font-bold", style: { background: theme.accent || "#f97316" } }, cta.button || "Get Started"));
};
const CoverPage = ({ data, theme, compact = false }) => /* @__PURE__ */ React.createElement("div", { className: compact ? "flyer-cover-compact" : "flyer-cover-full", style: { background: theme.background, borderRadius: 0 } }, data.logoPreview && /* @__PURE__ */ React.createElement(
  "div",
  {
    className: `mx-auto overflow-hidden bg-white/10 border border-white/20 ${compact ? "h-10 w-10 rounded-lg mb-2" : "h-16 w-16 rounded-xl mb-4"}`
  },
  /* @__PURE__ */ React.createElement("img", { src: data.logoPreview, alt: "Logo", className: "h-full w-full object-cover" })
), /* @__PURE__ */ React.createElement("p", { className: "text-white/60 font-bold uppercase tracking-[0.2em] mb-1.5", style: { fontSize: compact ? 8 : 10 } }, data.product?.productCategory || "Product Flyer"), /* @__PURE__ */ React.createElement("p", { className: "text-white font-black leading-tight", style: { fontSize: compact ? 15 : 26 } }, data.brand?.companyName || "Your Company"), /* @__PURE__ */ React.createElement("p", { className: "text-white/75 mt-1", style: { fontSize: compact ? 9 : 12 } }, data.brand?.brandTagline), data.product?.productName && /* @__PURE__ */ React.createElement("span", { className: "inline-block mt-3 rounded-full font-bold bg-white/15 border border-white/25 text-white", style: { fontSize: compact ? 8 : 10, padding: compact ? "3px 10px" : "5px 14px" } }, data.product.productName));
const RenderSection = ({ section, data, filtered, theme, dense = false }) => {
  const accent = theme.accent || "#f97316";
  switch (section.render || section.id) {
    case "overview":
      return /* @__PURE__ */ React.createElement("div", { className: dense ? "mb-3" : "mb-5" }, /* @__PURE__ */ React.createElement(SectionHeading, { accent }, "Overview"), /* @__PURE__ */ React.createElement("p", { className: `text-gray-600 leading-relaxed ${dense ? "text-[10px]" : "text-[11px]"}` }, data.product.productOverview));
    case "problemSolution":
      return /* @__PURE__ */ React.createElement("div", { className: dense ? "mb-3" : "mb-5" }, /* @__PURE__ */ React.createElement(SectionHeading, { accent }, "Problem & Solution"), data.problemStatement && /* @__PURE__ */ React.createElement("p", { className: `text-gray-600 leading-relaxed mb-2 ${dense ? "text-[10px]" : "text-[11px]"}` }, data.problemStatement), data.solutionStatement && /* @__PURE__ */ React.createElement("p", { className: `text-gray-600 leading-relaxed ${dense ? "text-[10px]" : "text-[11px]"}` }, data.solutionStatement));
    case "features":
      return /* @__PURE__ */ React.createElement("div", { className: dense ? "mb-3" : "mb-5" }, /* @__PURE__ */ React.createElement(SectionHeading, { accent }, "Features"), /* @__PURE__ */ React.createElement(FeatureGrid, { items: filtered.features.slice(0, dense ? 4 : 8), dense }));
    case "benefits":
      return /* @__PURE__ */ React.createElement("div", { className: dense ? "mb-3" : "mb-5" }, /* @__PURE__ */ React.createElement(SectionHeading, { accent }, "Benefits"), /* @__PURE__ */ React.createElement(FeatureGrid, { items: filtered.benefits.slice(0, dense ? 4 : 8), dense }));
    case "whyChooseUs":
      return /* @__PURE__ */ React.createElement("div", { className: dense ? "mb-3" : "mb-5" }, /* @__PURE__ */ React.createElement(SectionHeading, { accent }, "Why Choose Us"), /* @__PURE__ */ React.createElement(FeatureGrid, { items: filtered.why.slice(0, dense ? 3 : 8), dense }));
    case "screenshots":
      return /* @__PURE__ */ React.createElement("div", { className: dense ? "mb-3" : "mb-5" }, /* @__PURE__ */ React.createElement(SectionHeading, { accent }, "Gallery"), /* @__PURE__ */ React.createElement(ScreenshotGallery, { items: filtered.screenshots, maxCols: dense ? 3 : 2, dense }));
    case "pricing":
      return /* @__PURE__ */ React.createElement("div", { className: dense ? "mb-3" : "mb-5" }, /* @__PURE__ */ React.createElement(SectionHeading, { accent }, "Pricing"), /* @__PURE__ */ React.createElement(PricingBlock, { plans: filtered.plans, accent, dense }));
    case "testimonials":
      return /* @__PURE__ */ React.createElement("div", { className: dense ? "mb-3" : "mb-5" }, /* @__PURE__ */ React.createElement(SectionHeading, { accent }, "Testimonials"), /* @__PURE__ */ React.createElement(TestimonialsBlock, { items: filtered.testimonials, accent }));
    case "pricingTestimonials":
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: dense ? "mb-3" : "mb-5" }, /* @__PURE__ */ React.createElement(SectionHeading, { accent }, "Pricing"), /* @__PURE__ */ React.createElement(PricingBlock, { plans: filtered.plans, accent, dense })), /* @__PURE__ */ React.createElement("div", { className: dense ? "mb-3" : "mb-5" }, /* @__PURE__ */ React.createElement(SectionHeading, { accent }, "Testimonials"), /* @__PURE__ */ React.createElement(TestimonialsBlock, { items: filtered.testimonials, accent })));
    case "contact":
      return /* @__PURE__ */ React.createElement("div", { className: dense ? "mb-3" : "mb-5" }, /* @__PURE__ */ React.createElement(SectionHeading, { accent }, "Contact"), /* @__PURE__ */ React.createElement(ContactBlock, { contact: data.contact }));
    case "cta":
      return /* @__PURE__ */ React.createElement(CTABlock, { cta: data.cta, theme, compact: dense });
    default:
      return null;
  }
};
const OnePageFlyerEngine = ({ data, theme }) => {
  const filtered = getFilteredData(data);
  const accent = theme.accent || "#f97316";
  const availableH = INNER_H_PX;
  const allSections = buildSectionMetas(data, filtered);
  const footerHeight = (filtered.hasContact ? est.contact(true) : 0) + (filtered.hasCta ? est.cta(true) : 0) + est.gap(true);
  const headerH = est.header(true);
  const contentH = allSections.reduce((s, sec) => s + sec.height, 0);
  const totalH = headerH + contentH + footerHeight;
  let scale = totalH <= availableH ? 1 : Math.max(0.58, availableH / totalH);
  let visibleSections = allSections;
  if (totalH * scale > availableH) {
    const sorted = [...allSections].sort((a, b) => a.priority - b.priority);
    for (let i = 0; i < sorted.length; i++) {
      const tryDrop = sorted.slice(i + 1);
      const tryH = headerH + tryDrop.reduce((s, sec) => s + sec.height, 0) + footerHeight;
      const tryScale = Math.max(0.58, availableH / tryH);
      if (tryH * tryScale <= availableH) {
        const keepIds = new Set(tryDrop.map((s) => s.id));
        visibleSections = allSections.filter((s) => keepIds.has(s.id));
        scale = tryScale;
        break;
      }
    }
  }
  const gap = Math.max(6, Math.round(12 * scale));
  const fontSize = Math.max(9, Math.round(12 * scale));
  const headingSize = Math.max(10, Math.round(14 * scale));
  return /* @__PURE__ */ React.createElement(
    A4Page,
    {
      dense: true,
      accent,
      background: "#ffffff",
      bottomBleed: filtered.hasContact || filtered.hasCta ? /* @__PURE__ */ React.createElement("div", { className: "shrink-0", style: { marginTop: `${gap}px`, fontFamily: theme.font || "Inter, sans-serif", fontSize: `${fontSize}px`, lineHeight: 1.35 } }, filtered.hasContact && /* @__PURE__ */ React.createElement("div", { style: { padding: `0 ${mm(PAGE_MARGIN.x)}` } }, /* @__PURE__ */ React.createElement(RenderSection, { section: { id: "contact", render: "contact" }, data, filtered, theme, dense: true })), filtered.hasCta && /* @__PURE__ */ React.createElement("div", { style: { marginTop: filtered.hasContact ? `${gap}px` : 0 } }, /* @__PURE__ */ React.createElement(CTABlock, { cta: data.cta, theme, compact: true, squared: true }))) : null
    },
    /* @__PURE__ */ React.createElement("div", { className: "flex flex-col", style: { fontFamily: theme.font || "Inter, sans-serif", fontSize: `${fontSize}px`, lineHeight: 1.35 } }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2.5 mb-2 shrink-0", style: { marginBottom: `${gap}px` } }, data.logoPreview && /* @__PURE__ */ React.createElement("img", { src: data.logoPreview, alt: "Logo", className: "h-12 w-12 object-cover rounded-lg border border-gray-200 shrink-0", style: { transform: `scale(${scale})`, transformOrigin: "top left" } }), /* @__PURE__ */ React.createElement("div", { className: "min-w-0" }, /* @__PURE__ */ React.createElement("p", { className: "font-black text-gray-900 leading-tight", style: { fontSize: `${headingSize + 7}px` } }, data.brand?.companyName || "Your Company"), /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 font-semibold leading-tight mt-0.5", style: { fontSize: `${headingSize - 1}px` } }, data.product?.productName), data.brand?.brandTagline && /* @__PURE__ */ React.createElement("p", { className: "text-gray-400 leading-tight mt-0.5", style: { fontSize: `${headingSize - 4}px` } }, data.brand.brandTagline))), /* @__PURE__ */ React.createElement("div", { className: "flex-1 min-h-0 overflow-visible" }, visibleSections.map((sec) => /* @__PURE__ */ React.createElement("div", { key: sec.id, style: { marginBottom: `${gap}px` } }, /* @__PURE__ */ React.createElement(RenderSection, { section: sec, data, filtered, theme, dense: true })))))
  );
};
const MultiPageEngine = ({ data, theme, pageCount, config }) => {
  const filtered = getFilteredData(data);
  const accent = theme.accent || "#f97316";
  const { landscape, defaultPages, dense: baseDense } = config;
  const totalPages = Math.max(1, pageCount || defaultPages);
  const pageH = landscape ? INNER_H_LANDSCAPE_PX : INNER_H_PX;
  let dense = baseDense;
  let sections = buildSectionMetas(data, filtered);
  const computeLimits = (d) => {
    const coverH = est.coverCompact(d) + est.gap(d);
    const contactH = filtered.hasContact ? est.contact(d) + est.gap(d) : 0;
    const ctaH = filtered.hasCta ? est.cta(d) + est.gap(d) : 0;
    const footerH = contactH + ctaH;
    return Array.from({ length: totalPages }, (_, i) => {
      let limit = pageH;
      if (i === 0) limit -= coverH;
      if (i === totalPages - 1) limit -= footerH;
      return limit;
    });
  };
  let pageLimits = computeLimits(dense);
  let totalCapacity = pageLimits.reduce((s, l) => s + l, 0);
  let contentH = sections.reduce((s, sec) => s + sec.height, 0);
  if (contentH > totalCapacity) {
    dense = true;
    sections = buildSectionMetas(data, filtered).map((sec) => ({
      ...sec,
      height: Math.round(sec.height * 0.8)
    }));
    pageLimits = computeLimits(dense);
    totalCapacity = pageLimits.reduce((s, l) => s + l, 0);
    contentH = sections.reduce((s, sec) => s + sec.height, 0);
  }
  const distribute = (list, limits) => {
    const pages2 = limits.map(() => []);
    let p = 0, used = 0;
    for (const sec of list) {
      while (p < limits.length - 1 && used + sec.height > limits[p] && pages2[p].length > 0) {
        p += 1;
        used = 0;
      }
      pages2[p].push(sec);
      used += sec.height;
    }
    return pages2;
  };
  let pages = distribute(sections, pageLimits);
  const lastPageOverflows = () => {
    const last = pages[pages.length - 1];
    const lastH = last.reduce((s, sec) => s + sec.height, 0);
    return lastH > pageLimits[pageLimits.length - 1];
  };
  while (lastPageOverflows() && sections.length > 1) {
    const minPriority = Math.min(...sections.map((s) => s.priority));
    const dropIdx = sections.findIndex((s) => s.priority === minPriority);
    sections = sections.filter((_, i) => i !== dropIdx);
    pages = distribute(sections, pageLimits);
  }
  const renderPage = (secs, idx) => {
    const isFirst = idx === 0;
    const isLast = idx === pages.length - 1;
    return /* @__PURE__ */ React.createElement(
      A4Page,
      {
        key: `page-${idx}`,
        landscape,
        accent,
        background: "#ffffff",
        dense,
        topBleed: isFirst ? /* @__PURE__ */ React.createElement(CoverPage, { data, theme, compact: true }) : null,
        bottomBleed: isLast && filtered.hasCta ? /* @__PURE__ */ React.createElement(CTABlock, { cta: data.cta, theme, compact: dense, squared: true }) : null
      },
      /* @__PURE__ */ React.createElement("div", { className: "flex flex-col", style: { fontFamily: theme.font || "Inter, sans-serif" } }, /* @__PURE__ */ React.createElement("div", { className: isFirst ? dense ? "mt-2" : "mt-3" : "" }, secs.map((sec, sIdx) => /* @__PURE__ */ React.createElement(RenderSection, { key: `${sec.id}-${sIdx}`, section: sec, data, filtered, theme, dense }))), isLast && filtered.hasContact && /* @__PURE__ */ React.createElement("div", { className: dense ? "mt-2" : "mt-3" }, /* @__PURE__ */ React.createElement(RenderSection, { section: { id: "contact", render: "contact" }, data, filtered, theme, dense })))
    );
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, pages.map((secs, idx) => renderPage(secs, idx)));
};
const ModernIconBadge = ({ Icon, accent }) => /* @__PURE__ */ React.createElement("div", { className: "h-9 w-9 rounded-lg flex items-center justify-center shrink-0", style: { background: `${accent}1a`, color: accent } }, /* @__PURE__ */ React.createElement(Icon, { size: 16 }));
const ModernTemplatePreview = ({ data, theme }) => {
  const accent = theme.accent || "#f97316";
  const raw = getFilteredData(data);
  const filtered = {
    ...raw,
    features: raw.features.slice(0, 4),
    benefits: raw.benefits.slice(0, 4),
    why: raw.why.slice(0, 3),
    screenshots: raw.screenshots.slice(0, 1),
    plans: raw.plans.slice(0, 3),
    testimonials: raw.testimonials.slice(0, 2)
  };
  const iconCycle = [Sparkles, Layers, ArrowUpRight, Check, Rocket, Wand2];
  return /* @__PURE__ */ React.createElement("div", { className: "w-full max-w-3xl bg-white overflow-hidden shadow-2xl", style: { fontFamily: theme.font || "Inter, sans-serif" } }, /* @__PURE__ */ React.createElement("div", { className: "relative overflow-hidden p-8", style: { background: theme.background } }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2.5" }, data.logoPreview && /* @__PURE__ */ React.createElement("div", { className: "h-8 w-8 rounded-lg bg-white/10 border border-white/20 overflow-hidden shrink-0" }, /* @__PURE__ */ React.createElement("img", { src: data.logoPreview, alt: "Logo", className: "h-full w-full object-cover" })), /* @__PURE__ */ React.createElement("span", { className: "text-white font-extrabold text-sm" }, data.brand.companyName || "Your Company", " ", /* @__PURE__ */ React.createElement("span", { style: { color: accent } }, data.product.productCategory ? "" : "")), /* @__PURE__ */ React.createElement("span", { className: "text-white/50 text-xs ml-auto" }, data.brand.brandTagline)), /* @__PURE__ */ React.createElement("h1", { className: "text-white font-black text-4xl mt-6 leading-tight" }, data.product.productName || "Your Product"), /* @__PURE__ */ React.createElement("p", { className: "text-white/70 text-sm mt-2 max-w-md" }, data.product.productOverview || data.brand.brandTagline), data.product.productOverview && /* @__PURE__ */ React.createElement("p", { className: "mt-3", style: { color: accent } }, /* @__PURE__ */ React.createElement("span", { className: "text-white/70 text-xs" }, data.product.productCategory)), /* @__PURE__ */ React.createElement("div", { className: "absolute -right-6 -bottom-10 h-40 w-40 rounded-full opacity-20", style: { background: accent } })), /* @__PURE__ */ React.createElement("div", { className: "p-8 space-y-8" }, (data.problemStatement || data.solutionStatement) && /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-4" }, data.problemStatement && /* @__PURE__ */ React.createElement("div", { className: "rounded-xl p-4", style: { background: `${accent}0d` } }, /* @__PURE__ */ React.createElement("p", { className: "text-[10px] font-extrabold uppercase tracking-wider mb-1.5", style: { color: accent } }, "The Problem"), /* @__PURE__ */ React.createElement("p", { className: "text-[12px] text-gray-700 leading-relaxed" }, data.problemStatement)), data.solutionStatement && /* @__PURE__ */ React.createElement("div", { className: "rounded-xl p-4 bg-gray-50" }, /* @__PURE__ */ React.createElement("p", { className: "text-[10px] font-extrabold uppercase tracking-wider mb-1.5 text-gray-500" }, "Our Solution"), /* @__PURE__ */ React.createElement("p", { className: "text-[12px] text-gray-700 leading-relaxed" }, data.solutionStatement))), filtered.features.length > 0 && /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-extrabold uppercase tracking-wider mb-3", style: { color: accent } }, "Key Features"), /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-3" }, filtered.features.map((f, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-start gap-3" }, /* @__PURE__ */ React.createElement(ModernIconBadge, { Icon: iconCycle[i % iconCycle.length], accent }), /* @__PURE__ */ React.createElement("div", { className: "min-w-0" }, /* @__PURE__ */ React.createElement("p", { className: "text-[12px] font-bold text-gray-900" }, f.title), f.description && /* @__PURE__ */ React.createElement("p", { className: "text-[10.5px] text-gray-500 leading-snug mt-0.5" }, f.description)))))), (filtered.benefits.length > 0 || filtered.why.length > 0) && /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-6" }, filtered.benefits.length > 0 && /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-extrabold uppercase tracking-wider mb-3", style: { color: accent } }, "Key Benefits"), /* @__PURE__ */ React.createElement("div", { className: "space-y-3" }, filtered.benefits.map((b, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-start gap-3" }, /* @__PURE__ */ React.createElement(ModernIconBadge, { Icon: iconCycle[i % iconCycle.length], accent }), /* @__PURE__ */ React.createElement("div", { className: "min-w-0" }, /* @__PURE__ */ React.createElement("p", { className: "text-[12px] font-bold text-gray-900" }, b.title), b.description && /* @__PURE__ */ React.createElement("p", { className: "text-[10.5px] text-gray-500 leading-snug mt-0.5" }, b.description)))))), filtered.why.length > 0 && /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-extrabold uppercase tracking-wider mb-3", style: { color: accent } }, "Why Choose Us"), /* @__PURE__ */ React.createElement("div", { className: "space-y-3" }, filtered.why.map((w, i) => /* @__PURE__ */ React.createElement("div", { key: i, className: "flex items-start gap-3" }, /* @__PURE__ */ React.createElement(ModernIconBadge, { Icon: iconCycle[(i + 2) % iconCycle.length], accent }), /* @__PURE__ */ React.createElement("div", { className: "min-w-0" }, /* @__PURE__ */ React.createElement("p", { className: "text-[12px] font-bold text-gray-900" }, w.title), w.description && /* @__PURE__ */ React.createElement("p", { className: "text-[10.5px] text-gray-500 leading-snug mt-0.5" }, w.description))))))), filtered.screenshots.length > 0 && /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-extrabold uppercase tracking-wider mb-3", style: { color: accent } }, "Product Gallery"), /* @__PURE__ */ React.createElement(ScreenshotGallery, { items: filtered.screenshots, maxCols: filtered.screenshots.length >= 5 ? 3 : 2 })), data.pricingEnabled && filtered.plans.length > 0 && /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-extrabold uppercase tracking-wider mb-3", style: { color: accent } }, "Pricing"), /* @__PURE__ */ React.createElement(PricingBlock, { plans: filtered.plans, accent })), filtered.testimonials.length > 0 && /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-extrabold uppercase tracking-wider mb-3", style: { color: accent } }, "Testimonials"), /* @__PURE__ */ React.createElement(TestimonialsBlock, { items: filtered.testimonials, accent })), filtered.hasContact && /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-extrabold uppercase tracking-wider mb-3", style: { color: accent } }, "Get In Touch"), /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" }, data.contact.phone && /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2.5 min-w-0" }, /* @__PURE__ */ React.createElement(ModernIconBadge, { Icon: Phone, accent }), /* @__PURE__ */ React.createElement("span", { className: "text-[11px] text-gray-700 leading-snug min-w-0 flex-1 [overflow-wrap:anywhere]" }, data.contact.phone)), data.contact.email && /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2.5 min-w-0" }, /* @__PURE__ */ React.createElement(ModernIconBadge, { Icon: Mail, accent }), /* @__PURE__ */ React.createElement("span", { className: "text-[11px] text-gray-700 leading-snug min-w-0 flex-1 [overflow-wrap:anywhere]" }, data.contact.email)), data.contact.website && /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2.5 min-w-0" }, /* @__PURE__ */ React.createElement(ModernIconBadge, { Icon: Globe, accent }), /* @__PURE__ */ React.createElement("span", { className: "text-[11px] text-gray-700 leading-snug min-w-0 flex-1 [overflow-wrap:anywhere]" }, data.contact.website)), data.contact.address && /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2.5 min-w-0" }, /* @__PURE__ */ React.createElement(ModernIconBadge, { Icon: MapPin, accent }), /* @__PURE__ */ React.createElement("span", { className: "text-[11px] text-gray-700 leading-snug min-w-0 flex-1 [overflow-wrap:anywhere]" }, data.contact.address))))), filtered.hasCta && /* @__PURE__ */ React.createElement("div", { className: "flex items-center justify-between gap-4 px-8 py-6 bg-gray-950" }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("p", { className: "text-white font-extrabold text-sm" }, data.cta.heading || "Ready to get started?"), data.cta.subtext && /* @__PURE__ */ React.createElement("p", { className: "text-gray-400 text-xs mt-1" }, data.cta.subtext)), /* @__PURE__ */ React.createElement("span", { className: "shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white", style: { background: accent } }, data.cta.button || "Get Started", " ", /* @__PURE__ */ React.createElement(ArrowUpRight, { size: 13 }))));
};
const TwoPageEngine = ({ data, theme }) => /* @__PURE__ */ React.createElement(MultiPageEngine, { data, theme, pageCount: 2, config: { landscape: false, defaultPages: 2, dense: false } });
const FlyerPreview = ({ layout, template = "classic", theme, brand, product, problemStatement, solutionStatement, features, benefits, whyChooseUs, screenshots, pricingEnabled, pricingPlans, testimonials, contact, cta, logoPreview }) => {
  const data = {
    brand,
    product,
    problemStatement,
    solutionStatement,
    features,
    benefits,
    whyChooseUs,
    screenshots,
    pricingEnabled,
    pricingPlans,
    testimonials,
    contact,
    cta,
    logoPreview
  };
  const renderEngine = () => {
    if (template === "modern") {
      return /* @__PURE__ */ React.createElement(ModernTemplatePreview, { data, theme });
    }
    switch (layout) {
      case "onePage":
        return /* @__PURE__ */ React.createElement(OnePageFlyerEngine, { data, theme });
      case "twoPage":
        return /* @__PURE__ */ React.createElement(TwoPageEngine, { data, theme });
      default:
        return /* @__PURE__ */ React.createElement(OnePageFlyerEngine, { data, theme });
    }
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("style", null, `
                .flyer-page-frame { break-inside: avoid; page-break-inside: avoid; }
                .flyer-page { box-sizing: border-box; overflow: visible; display: flex; flex-direction: column; box-shadow: 0 18px 40px -12px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.04); }
                .flyer-page-inner { flex: 0 1 auto; overflow: visible; }
                .flyer-page-dense .flyer-page-inner { font-size: 10.5px; }
                .flyer-page-footer { margin-top: auto; padding-top: 6px; border-top: 1px solid; display: flex; justify-content: space-between; font-size: 7.5px; letter-spacing: 0.08em; text-transform: uppercase; color: #9ca3af; }
                .flyer-cover-full { min-height: 100%; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: ${mm(20)} ${mm(14)}; }
                .flyer-cover-compact { text-align: center; padding: 14px 12px; margin-bottom: 10px; border-radius: 12px; }
                @media print {
                    body * { visibility: hidden; }
                    #flyer-print-root, #flyer-print-root * { visibility: visible; }
                    #flyer-print-root { position: absolute; left: 0; top: 0; width: auto; }
                    #flyer-print-root [data-fit-outer] { overflow: visible !important; height: auto !important; width: auto !important; }
                    #flyer-print-root [data-fit-inner] { transform: none !important; margin-left: 0 !important; width: auto !important; }
                    .flyer-page-frame { box-shadow: none !important; margin: 0 !important; break-after: page; page-break-after: always; }
                    .flyer-page-frame:last-child { break-after: auto; page-break-after: auto; }
                    .flyer-page { box-shadow: none !important; overflow: hidden !important; width: ${A4.w}mm !important; height: ${A4.h}mm !important; }
                    .flyer-page[data-landscape="true"] { width: ${A4.h}mm !important; height: ${A4.w}mm !important; }
                }
            `), /* @__PURE__ */ React.createElement("div", { className: "flex flex-col items-center gap-6 w-fit mx-auto" }, renderEngine()));
};
const FIT_CARD_WIDTH = 760;
const FitCard = ({ children, maxHeight, designWidth = FIT_CARD_WIDTH, minScale = 0.45, heightOffset = 110, fitFirstPage = false }) => {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [dims, setDims] = useState({ w: designWidth || 0, h: 0 });
  useLayoutEffect(() => {
    const compute = () => {
      if (!outerRef.current || !innerRef.current) return;
      const availW = outerRef.current.clientWidth;
      const naturalW = designWidth || innerRef.current.offsetWidth;
      const h = innerRef.current.offsetHeight;
      const widthFit = availW > 0 && naturalW > 0 ? Math.min(1, availW / naturalW) : 1;
      let s = widthFit;
      if (maxHeight && h > 0 && window.innerWidth >= 1024) {
        const pageEl = fitFirstPage ? innerRef.current.querySelector(".flyer-page") : null;
        const fitH = pageEl && pageEl.offsetHeight ? pageEl.offsetHeight : h;
        const availH = maxHeight - heightOffset;
        if (availH > 0) s = Math.min(widthFit, Math.max(minScale, availH / fitH));
      }
      setScale((prev) => Math.abs(prev - s) > 5e-3 ? s : prev);
      setDims((prev) => prev.w !== naturalW || prev.h !== h ? { w: naturalW, h } : prev);
    };
    compute();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(compute) : null;
    if (ro) {
      ro.observe(outerRef.current);
      ro.observe(innerRef.current);
    }
    window.addEventListener("resize", compute);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [maxHeight, designWidth, minScale, heightOffset, fitFirstPage]);
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      ref: outerRef,
      "data-fit-outer": "true",
      style: { width: "100%", height: dims.h ? dims.h * scale : void 0, overflow: "hidden" }
    },
    /* @__PURE__ */ React.createElement(
      "div",
      {
        ref: innerRef,
        "data-fit-inner": "true",
        style: {
          width: designWidth || "max-content",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          marginLeft: `max(0px, calc((100% - ${dims.w * scale}px) / 2))`
        }
      },
      children
    )
  );
};
const useAvailableHeight = (bottomPadding = 24) => {
  const ref = useRef(null);
  const [maxHeight, setMaxHeight] = useState(null);
  useLayoutEffect(() => {
    const compute = () => {
      if (!ref.current) return;
      if (window.innerWidth < 1024) {
        setMaxHeight(null);
        return;
      }
      const top = ref.current.getBoundingClientRect().top;
      setMaxHeight(Math.max(240, window.innerHeight - top - bottomPadding));
    };
    compute();
    window.addEventListener("resize", compute);
    const id = window.setInterval(compute, 500);
    return () => {
      window.removeEventListener("resize", compute);
      window.clearInterval(id);
    };
  }, [bottomPadding]);
  return [ref, maxHeight];
};
const FlyerGenerationView = () => {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [brand, setBrand] = useState({ companyName: "", brandTagline: "" });
  const [product, setProduct] = useState({ productName: "", productCategory: "", productOverview: "" });
  const [problemStatement, setProblemStatement] = useState("");
  const [solutionStatement, setSolutionStatement] = useState("");
  const [features, setFeatures] = useState([emptyFeature()]);
  const [benefits, setBenefits] = useState([emptyFeature()]);
  const [whyChooseUs, setWhyChooseUs] = useState([emptyFeature()]);
  const [screenshots, setScreenshots] = useState([]);
  const [pricingEnabled, setPricingEnabled] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([emptyPricingPlan()]);
  const [testimonials, setTestimonials] = useState([emptyTestimonial()]);
  const [contact, setContact] = useState({ phone: "", email: "", website: "", address: "" });
  const [cta, setCta] = useState({ heading: "", subtext: "", button: "Get Started" });
  const [layout, setLayout] = useState(FLYER_LAYOUTS[0].id);
  const [activeFormTab, setActiveFormTab] = useState(FLYER_FORM_TABS[0].id);
  const [template, setTemplate] = useState(FLYER_TEMPLATES[0].id);
  const [formScrollRef, formMaxHeight] = useAvailableHeight(24);
  const [previewScrollRef, previewMaxHeight] = useAvailableHeight(24);
  const effectivePageCount = layout === "onePage" ? 1 : 2;
  const {
    aiPrompt,
    setAiPrompt,
    aiThemeLoading,
    aiThemeError,
    aiTheme,
    generate: generateAiTheme,
    reset: handleResetTheme
  } = useAiThemeGenerator();
  const [selectedFontId, setSelectedFontId] = useState(FONT_OPTIONS[0].id);
  const [customFontName, setCustomFontName] = useState("");
  useGoogleFonts([...FONT_OPTIONS.map((f) => f.google), customFontName.trim() ? customFontName.trim().replace(/\s+/g, "+") : null]);
  const selectedFontStack = selectedFontId === "other" ? customFontName.trim() ? `'${customFontName.trim()}', sans-serif` : FONT_OPTIONS[0].stack : FONT_OPTIONS.find((f) => f.id === selectedFontId)?.stack || FONT_OPTIONS[0].stack;
  const activeTheme = { ...aiTheme || DEFAULT_FLYER_THEME, font: selectedFontStack };
  const [aiContentForm, setAiContentForm] = useState({ companyName: "", productName: "", industry: "", website: "" });
  const [aiContentLoading, setAiContentLoading] = useState(false);
  const [aiContentError, setAiContentError] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [meRes, productsRes] = await Promise.allSettled([
        fetch(ME_ENDPOINT, { headers: authHeaders() }),
        fetch(PRODUCTS_ENDPOINT, { headers: authHeaders() })
      ]);
      const me = meRes.status === "fulfilled" && meRes.value.ok ? await meRes.value.json().catch(() => null) : null;
      const productsData = productsRes.status === "fulfilled" && productsRes.value.ok ? await productsRes.value.json().catch(() => null) : null;
      const firstProduct = productsData?.data?.[0];
      if (cancelled || (!me && !firstProduct)) return;
      setAiContentForm((prev) => ({
        companyName: prev.companyName || me?.company_name || "",
        productName: prev.productName || firstProduct?.product_name || "",
        industry: prev.industry || me?.industry_sector || "",
        website: prev.website || me?.website || ""
      }));
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const [downloadError, setDownloadError] = useState("");
  const [downloadingPreview, setDownloadingPreview] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("");
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const previewRef = useRef(null);
  const handleDownloadPreview = async (fmt) => {
    if (!previewRef.current || downloadingPreview) return;
    setDownloadingPreview(true);
    setDownloadFormat(fmt);
    setDownloadMenuOpen(false);
    setDownloadError("");
    try {
      const { default: html2canvas } = await import("html2canvas-pro");
      const canvas = await html2canvas(previewRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        onclone: (doc) => {
          doc.querySelectorAll("[data-fit-inner]").forEach((el) => {
            el.style.transform = "none";
            el.style.marginLeft = "0";
            el.style.width = "max-content";
          });
          doc.querySelectorAll("[data-fit-outer]").forEach((el) => {
            el.style.overflow = "visible";
            el.style.height = "auto";
            el.style.width = "max-content";
          });
        }
      });
      const slug = (brand.companyName || product.productName).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "flyer";
      if (fmt === "pdf") {
        const { jsPDF } = await import("jspdf");
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: canvas.width >= canvas.height ? "landscape" : "portrait",
          unit: "px",
          format: [canvas.width, canvas.height]
        });
        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
        pdf.save(`${slug}-flyer.pdf`);
      } else {
        const link = document.createElement("a");
        link.download = `${slug}-flyer.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
      }
    } catch (err) {
      console.error("Flyer preview export failed:", err);
      setDownloadError(
        `Couldn't export the preview: ${err?.message || err}. Make sure \`npm install html2canvas-pro jspdf\` has been run and the dev server was restarted.`
      );
    } finally {
      setDownloadingPreview(false);
      setDownloadFormat("");
    }
  };
  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(await fileToDataUrl(file));
  };
  const handleAddScreenshots = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, MAX_SCREENSHOTS - screenshots.length);
    const converted = await Promise.all(files.map(async (f) => ({ dataUrl: await fileToDataUrl(f), caption: "" })));
    setScreenshots((prev) => [...prev, ...converted].slice(0, MAX_SCREENSHOTS));
  };
  const removeScreenshot = (i) => setScreenshots((prev) => prev.filter((_, idx) => idx !== i));
  const updateScreenshotCaption = (i, val) => setScreenshots((prev) => prev.map((s, idx) => idx === i ? { ...s, caption: val } : s));
  const handleFillDemoData = () => {
    setBrand({ companyName: "Northwind Robotics", brandTagline: "Automation that pays for itself in 90 days" });
    setProduct({
      productName: "Northwind FlowLine",
      productCategory: "Warehouse Automation",
      productOverview: "FlowLine is a modular conveyor-and-robotics kit that bolts onto your existing warehouse layout, cutting manual pick-and-pack time by more than half without a full facility redesign."
    });
    setProblemStatement(
      "Manual pick-and-pack workflows can't keep up with peak-season order volume, leading to missed SLAs, overtime costs, and burned-out warehouse staff."
    );
    setSolutionStatement(
      "FlowLine adds robotic picking arms and smart conveyor routing to your current racking, guided by an AI queue optimizer that rebalances work in real time as orders spike."
    );
    setFeatures([
      { title: "Smart Pick Routing", description: "AI reroutes orders to the fastest open picking station automatically." },
      { title: "Plug-and-Play Install", description: "Bolts onto existing racking \u2014 no facility redesign or downtime week." },
      { title: "Live Throughput Dashboard", description: "Real-time visibility into orders/hour, bottlenecks, and staffing needs." },
      { title: "Modular Expansion", description: "Add lanes or arms as volume grows, without replacing the base system." }
    ]);
    setBenefits([
      { title: "58% Faster Pick Times", description: "Average across pilot customers in the first 90 days." },
      { title: "Lower Overtime Spend", description: "Smooths peak-season spikes instead of relying on extra shifts." },
      { title: "Fewer Mis-Picks", description: "Guided picking cuts order errors and costly returns." }
    ]);
    setWhyChooseUs([
      { title: "12-Week Install", description: "Fastest deployment timeline in the category, start to finish." },
      { title: "Dedicated Success Team", description: "A named engineer stays on your account for the first year." },
      { title: "Hardware-Agnostic", description: "Works with most existing conveyor and racking systems." }
    ]);
    setPricingEnabled(true);
    setPricingPlans([
      { name: "Starter", price: "$1,499", period: "/ month", featuresText: "1 lane, Live dashboard, Email support", highlighted: false },
      { name: "Growth", price: "$3,999", period: "/ month", featuresText: "Up to 4 lanes, Priority support, Quarterly tuning", highlighted: true },
      { name: "Enterprise", price: "Custom", period: "", featuresText: "Unlimited lanes, Dedicated engineer, Custom SLAs", highlighted: false }
    ]);
    setTestimonials([
      { quote: "FlowLine cut our peak-season overtime bill in half within one quarter.", author: "Priya Nair", role: "Ops Director, Cascade Retail" },
      { quote: "Installed in under three weeks with zero disruption to live shipping.", author: "Marcus Webb", role: "Warehouse Manager, Ironclad Supply" }
    ]);
    setContact({ phone: "+1 (555) 019-4482", email: "hello@northwindrobotics.com", website: "www.northwindrobotics.com", address: "480 Harbor Way, Austin, TX" });
    setCta({ heading: "Ready to see FlowLine in your warehouse?", subtext: "Book a 20-minute walkthrough \u2014 no commitment required.", button: "Book a Demo" });
  };
  const handleGenerateAiContent = async () => {
    const { companyName, productName, industry, website } = aiContentForm;
    if (!companyName.trim() || !productName.trim() || aiContentLoading) return;
    setAiContentLoading(true);
    setAiContentError("");
    try {
      const res = await fetch(FLYER_AI_CONTENT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ company_name: companyName, product_name: productName, industry, website, page_count: effectivePageCount })
      });
      const resp = await res.json().catch(() => null);
      if (!res.ok || !resp?.content) {
        throw new Error(resp?.detail || resp?.message || `Request failed (${res.status})`);
      }
      const c = resp.content;
      setBrand({ companyName, brandTagline: c.brand_tagline || "" });
      setProduct((prev) => ({ ...prev, productName, productOverview: c.product_overview || "" }));
      setProblemStatement(c.problem_statement || "");
      setSolutionStatement(c.solution_statement || "");
      if (Array.isArray(c.features) && c.features.length) {
        setFeatures(c.features.slice(0, FLYER_MAX_ITEMS).map((f) => ({ title: f.title || "", description: f.description || "" })));
      }
      if (Array.isArray(c.benefits) && c.benefits.length) {
        setBenefits(c.benefits.slice(0, FLYER_MAX_ITEMS).map((f) => ({ title: f.title || "", description: f.description || "" })));
      }
      if (Array.isArray(c.why_choose_us) && c.why_choose_us.length) {
        setWhyChooseUs(c.why_choose_us.slice(0, FLYER_MAX_ITEMS).map((f) => ({ title: f.title || "", description: f.description || "" })));
      }
      setCta((prev) => ({ ...prev, heading: c.cta_heading || prev.heading, subtext: c.cta_subtext || prev.subtext }));
    } catch (err) {
      setAiContentError(
        `Couldn't generate content: ${err?.message || err}. Make sure the backend is running on ${GROWTHOS_API_BASE}.`
      );
    } finally {
      setAiContentLoading(false);
    }
  };
  return /* @__PURE__ */ React.createElement("div", { className: "mt-6 lg:flex-1 lg:min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-6 pb-2" }, /* @__PURE__ */ React.createElement("div", { ref: formScrollRef, className: "thin-scrollbar space-y-5 lg:overflow-y-auto lg:pr-3", style: { maxHeight: formMaxHeight ? `${formMaxHeight}px` : void 0 } }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center justify-between rounded-2xl border border-dashed border-orange-500/25 bg-orange-500/[0.04] px-4 py-3" }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2 text-xs text-gray-400" }, /* @__PURE__ */ React.createElement(Wand2, { size: 14, className: "text-orange-400 shrink-0" }), "Just testing? Fill the form with sample content in one click."), /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: handleFillDemoData,
      className: "inline-flex items-center gap-1.5 shrink-0 bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/30 text-orange-300 text-xs font-semibold rounded-lg px-3 py-1.5 transition"
    },
    /* @__PURE__ */ React.createElement(Wand2, { size: 13 }),
    " Demo"
  )), downloadError && /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2.5 text-[13px] text-red-400 border border-red-500/20 bg-red-500/[0.06] rounded-2xl px-4 py-3.5" }, /* @__PURE__ */ React.createElement(AlertCircle, { size: 16, className: "shrink-0" }), " ", downloadError), /* @__PURE__ */ React.createElement(
    SectionCard,
    {
      icon: Hash,
      title: "Number of Pages",
      subtitle: "Set automatically by the layout you choose below"
    },
    layout === "onePage" ? /* @__PURE__ */ React.createElement("p", { className: "text-[11px] text-gray-400 leading-relaxed" }, "One Page always generates exactly", " ", /* @__PURE__ */ React.createElement("span", { className: "text-orange-400 font-semibold" }, "1 page"), ". Content is auto-prioritized \u2014 logo \u2192 company \u2192 product \u2192 overview \u2192 problem & solution \u2192 top 4 features \u2192 top 4 benefits \u2192 top 3 why-choose-us \u2192 1 screenshot \u2192 top 3 pricing plans \u2192 top 2 testimonials \u2192 contact \u2192 CTA \u2014 and font size, spacing, and padding shrink automatically so everything fits with no overflow and nothing is hidden. The CTA always stays pinned to the bottom.") : /* @__PURE__ */ React.createElement("p", { className: "text-[11px] text-gray-400 leading-relaxed" }, "Two Page always generates exactly", " ", /* @__PURE__ */ React.createElement("span", { className: "text-orange-400 font-semibold" }, "2 pages"), ": a cover page, then one content page with every section \u2014 features, benefits, why-choose-us, screenshots, pricing, and testimonials \u2014 followed by contact and the CTA pinned to the bottom of that same page.")
  ), /* @__PURE__ */ React.createElement(SectionCard, { icon: Rocket, title: "AI Content Generator", subtitle: "Draft the whole flyer from four fields" }, aiContentError && /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2 text-xs text-red-400 border border-red-500/20 bg-red-500/[0.06] rounded-xl px-3 py-2.5" }, /* @__PURE__ */ React.createElement(AlertCircle, { size: 13, className: "shrink-0" }), " ", aiContentError), /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-3" }, /* @__PURE__ */ React.createElement(
    "input",
    {
      className: inputClass,
      placeholder: "Company name",
      value: aiContentForm.companyName,
      onChange: (e) => setAiContentForm((p) => ({ ...p, companyName: e.target.value }))
    }
  ), /* @__PURE__ */ React.createElement(
    "input",
    {
      className: inputClass,
      placeholder: "Product name",
      value: aiContentForm.productName,
      onChange: (e) => setAiContentForm((p) => ({ ...p, productName: e.target.value }))
    }
  ), /* @__PURE__ */ React.createElement(
    "input",
    {
      className: inputClass,
      placeholder: "Industry",
      value: aiContentForm.industry,
      onChange: (e) => setAiContentForm((p) => ({ ...p, industry: e.target.value }))
    }
  ), /* @__PURE__ */ React.createElement(
    "input",
    {
      className: inputClass,
      placeholder: "Website (optional)",
      value: aiContentForm.website,
      onChange: (e) => setAiContentForm((p) => ({ ...p, website: e.target.value }))
    }
  )), /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: handleGenerateAiContent,
      disabled: !aiContentForm.companyName.trim() || !aiContentForm.productName.trim() || aiContentLoading,
      className: "w-full inline-flex items-center justify-center gap-2 bg-orange-500/15 hover:bg-orange-500/25 backdrop-blur-sm border border-orange-400/30 disabled:opacity-40 disabled:cursor-not-allowed text-orange-200 text-xs font-semibold rounded-lg px-3.5 py-2 transition"
    },
    aiContentLoading ? /* @__PURE__ */ React.createElement(Loader2, { size: 13, className: "animate-spin" }) : /* @__PURE__ */ React.createElement(Sparkles, { size: 13 }),
    aiContentLoading ? "Generating..." : "Generate Flyer with AI"
  ), /* @__PURE__ */ React.createElement("p", { className: "text-[11px] text-gray-500" }, "Everything it drafts stays fully editable in the sections below.")), /* @__PURE__ */ React.createElement(SectionCard, { icon: Layers, title: "Flyer Layout", subtitle: "Changes page structure \u2014 not your content" }, /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-2.5" }, FLYER_LAYOUTS.map((l) => {
    const Icon = l.icon;
    const active = layout === l.id;
    const disabledByTemplate = template === "modern" && l.id !== "onePage";
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        key: l.id,
        type: "button",
        disabled: disabledByTemplate,
        onClick: () => !disabledByTemplate && setLayout(l.id),
        title: disabledByTemplate ? "Modern template currently only supports One Page" : void 0,
        className: `text-left rounded-xl border px-3.5 py-3 transition ${disabledByTemplate ? "border-white/5 bg-white/[0.01] opacity-40 blur-[1px] cursor-not-allowed pointer-events-none" : active ? "border-orange-500/60 bg-orange-500/[0.08]" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`
      },
      /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2" }, /* @__PURE__ */ React.createElement(Icon, { size: 14, className: active && !disabledByTemplate ? "text-orange-400" : "text-gray-400" }), /* @__PURE__ */ React.createElement("span", { className: `text-xs font-semibold ${active && !disabledByTemplate ? "text-orange-300" : "text-gray-200"}` }, l.label), active && !disabledByTemplate && /* @__PURE__ */ React.createElement(Check, { size: 12, className: "text-orange-400 ml-auto" }))
    );
  })), template === "modern" && /* @__PURE__ */ React.createElement("p", { className: "text-[10.5px] text-gray-500 leading-snug" }, "Modern template is currently One Page only \u2014 switch back to Classic under Flyer Template to unlock Two Page.")), /* @__PURE__ */ React.createElement(SectionCard, { icon: Layers, title: "Flyer Template", subtitle: "Choose the visual design \u2014 content stays the same" }, /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-2.5" }, FLYER_TEMPLATES.map((t) => {
    const active = template === t.id;
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        key: t.id,
        type: "button",
        onClick: () => {
          setTemplate(t.id);
          if (t.id === "modern") setLayout("onePage");
        },
        className: `text-left rounded-xl border px-3.5 py-3 transition ${active ? "border-orange-500/60 bg-orange-500/[0.08]" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`
      },
      /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2" }, /* @__PURE__ */ React.createElement("span", { className: `text-xs font-semibold ${active ? "text-orange-300" : "text-gray-200"}` }, t.label), t.id === "classic" && /* @__PURE__ */ React.createElement("span", { className: "text-[9px] text-gray-500 border border-white/10 rounded-full px-1.5 py-0.5" }, "Default"), active && /* @__PURE__ */ React.createElement(Check, { size: 12, className: "text-orange-400 ml-auto" }))
    );
  }))), /* @__PURE__ */ React.createElement("div", { className: "rounded-2xl border border-white/5 bg-white/[0.02]" }, /* @__PURE__ */ React.createElement("div", { className: "flex flex-col sm:flex-row" }, /* @__PURE__ */ React.createElement("div", { className: "sm:w-56 shrink-0 border-b sm:border-b-0 sm:border-r border-white/5 p-2 flex flex-row sm:flex-col gap-1 overflow-x-auto sm:overflow-visible" }, FLYER_FORM_TABS.map((t) => {
    const TabIcon = t.icon;
    const active = activeFormTab === t.id;
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        key: t.id,
        type: "button",
        onClick: () => setActiveFormTab(t.id),
        className: `shrink-0 sm:w-full flex items-center gap-2.5 text-left rounded-xl px-3 py-2.5 text-xs font-semibold transition whitespace-nowrap ${active ? "bg-orange-500/10 text-orange-300 border border-orange-500/30" : "text-gray-400 hover:bg-white/[0.04] hover:text-gray-200 border border-transparent"}`
      },
      /* @__PURE__ */ React.createElement(TabIcon, { size: 14, className: active ? "text-orange-400" : "text-gray-500" }),
      t.label
    );
  })), /* @__PURE__ */ React.createElement("div", { className: "flex-1 min-w-0 p-5 space-y-4" }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h4", { className: "text-white text-sm font-semibold leading-tight" }, FLYER_FORM_TABS.find((t) => t.id === activeFormTab)?.label), /* @__PURE__ */ React.createElement("p", { className: "text-gray-500 text-xs mt-0.5" }, FLYER_FORM_TABS.find((t) => t.id === activeFormTab)?.subtitle)), activeFormTab === "brand" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-4" }, /* @__PURE__ */ React.createElement("div", { className: "h-16 w-16 rounded-2xl border border-dashed border-orange-500/30 bg-white/[0.03] flex items-center justify-center overflow-hidden shrink-0" }, logoPreview ? /* @__PURE__ */ React.createElement("img", { src: logoPreview, alt: "Logo preview", className: "h-full w-full object-cover" }) : /* @__PURE__ */ React.createElement(ImageIcon, { size: 20, className: "text-gray-600" })), /* @__PURE__ */ React.createElement("label", { className: "inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-semibold rounded-xl px-4 py-2.5 cursor-pointer transition" }, /* @__PURE__ */ React.createElement(Upload, { size: 13 }), logoFile ? "Change logo" : "Upload logo", /* @__PURE__ */ React.createElement("input", { type: "file", accept: "image/*", className: "hidden", onChange: handleLogoChange }))), /* @__PURE__ */ React.createElement("div", { className: "rounded-2xl border border-white/10 bg-gradient-to-br from-orange-500/[0.06] to-transparent p-4 space-y-4" }, /* @__PURE__ */ React.createElement(Field, { label: "Company Name", required: true }, /* @__PURE__ */ React.createElement(
    IconInput,
    {
      icon: Building2,
      className: "text-base font-semibold",
      placeholder: "Acme Inc.",
      value: brand.companyName,
      onChange: (e) => setBrand((p) => ({ ...p, companyName: e.target.value }))
    }
  )), /* @__PURE__ */ React.createElement(Field, { label: "Brand Tagline" }, /* @__PURE__ */ React.createElement(
    IconInput,
    {
      icon: Quote,
      placeholder: "Smart. Simple. Powerful.",
      value: brand.brandTagline,
      onChange: (e) => setBrand((p) => ({ ...p, brandTagline: e.target.value }))
    }
  )))), activeFormTab === "product" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-4" }, /* @__PURE__ */ React.createElement(Field, { label: "Product Name", required: true }, /* @__PURE__ */ React.createElement(
    "input",
    {
      className: inputClass,
      placeholder: "Your product name",
      value: product.productName,
      onChange: (e) => setProduct((p) => ({ ...p, productName: e.target.value }))
    }
  )), /* @__PURE__ */ React.createElement(Field, { label: "Category" }, /* @__PURE__ */ React.createElement(
    "input",
    {
      className: inputClass,
      placeholder: "e.g. SaaS Platform",
      value: product.productCategory,
      onChange: (e) => setProduct((p) => ({ ...p, productCategory: e.target.value }))
    }
  ))), /* @__PURE__ */ React.createElement(Field, { label: "Overview" }, /* @__PURE__ */ React.createElement(
    "textarea",
    {
      className: `${inputClass} resize-none`,
      rows: 3,
      placeholder: "2-3 sentences describing the product",
      value: product.productOverview,
      onChange: (e) => setProduct((p) => ({ ...p, productOverview: e.target.value }))
    }
  ))), activeFormTab === "problem" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Field, { label: "Problem Statement" }, /* @__PURE__ */ React.createElement(
    "textarea",
    {
      className: `${inputClass} resize-none`,
      rows: 2,
      placeholder: "What pain point does your customer have?",
      value: problemStatement,
      onChange: (e) => setProblemStatement(e.target.value)
    }
  )), /* @__PURE__ */ React.createElement(Field, { label: "Solution" }, /* @__PURE__ */ React.createElement(
    "textarea",
    {
      className: `${inputClass} resize-none`,
      rows: 2,
      placeholder: "How does your product solve it?",
      value: solutionStatement,
      onChange: (e) => setSolutionStatement(e.target.value)
    }
  ))), activeFormTab === "features" && /* @__PURE__ */ React.createElement(
    TitleDescListEditor,
    {
      items: features,
      onUpdate: updateListItem(setFeatures),
      onAdd: addListItem(setFeatures, FLYER_MAX_ITEMS, emptyFeature),
      onRemove: removeListItem(setFeatures),
      max: FLYER_MAX_ITEMS,
      itemLabel: "Feature"
    }
  ), activeFormTab === "benefits" && /* @__PURE__ */ React.createElement(
    TitleDescListEditor,
    {
      items: benefits,
      onUpdate: updateListItem(setBenefits),
      onAdd: addListItem(setBenefits, FLYER_MAX_ITEMS, emptyFeature),
      onRemove: removeListItem(setBenefits),
      max: FLYER_MAX_ITEMS,
      itemLabel: "Benefit"
    }
  ), activeFormTab === "why" && /* @__PURE__ */ React.createElement(
    TitleDescListEditor,
    {
      items: whyChooseUs,
      onUpdate: updateListItem(setWhyChooseUs),
      onAdd: addListItem(setWhyChooseUs, FLYER_MAX_ITEMS, emptyFeature),
      onRemove: removeListItem(setWhyChooseUs),
      max: FLYER_MAX_ITEMS,
      itemLabel: "Reason"
    }
  ), activeFormTab === "media" && /* @__PURE__ */ React.createElement(
    ScreenshotUploader,
    {
      screenshots,
      onAddFiles: handleAddScreenshots,
      onRemove: removeScreenshot,
      onCaptionChange: updateScreenshotCaption,
      max: MAX_SCREENSHOTS
    }
  ), activeFormTab === "pricing" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("label", { className: "inline-flex items-center gap-2 text-xs text-gray-400" }, /* @__PURE__ */ React.createElement("input", { type: "checkbox", checked: pricingEnabled, onChange: (e) => setPricingEnabled(e.target.checked) }), "Include pricing"), pricingEnabled && /* @__PURE__ */ React.createElement(
    PricingPlansEditor,
    {
      plans: pricingPlans,
      onUpdate: updateListItem(setPricingPlans),
      onAdd: addListItem(setPricingPlans, MAX_PRICING_PLANS, emptyPricingPlan),
      onRemove: removeListItem(setPricingPlans),
      max: MAX_PRICING_PLANS
    }
  )), activeFormTab === "testimonials" && /* @__PURE__ */ React.createElement(
    TestimonialsEditor,
    {
      items: testimonials,
      onUpdate: updateListItem(setTestimonials),
      onAdd: addListItem(setTestimonials, MAX_TESTIMONIALS, emptyTestimonial),
      onRemove: removeListItem(setTestimonials),
      max: MAX_TESTIMONIALS
    }
  ), activeFormTab === "contact" && /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-4" }, /* @__PURE__ */ React.createElement(Field, { label: "Phone" }, /* @__PURE__ */ React.createElement(IconInput, { icon: Phone, placeholder: "+91 XXXXXXXXXX", value: contact.phone, onChange: (e) => setContact((p) => ({ ...p, phone: e.target.value })) })), /* @__PURE__ */ React.createElement(Field, { label: "Email" }, /* @__PURE__ */ React.createElement(IconInput, { icon: Mail, placeholder: "you@company.com", value: contact.email, onChange: (e) => setContact((p) => ({ ...p, email: e.target.value })) })), /* @__PURE__ */ React.createElement(Field, { label: "Website" }, /* @__PURE__ */ React.createElement(IconInput, { icon: Globe, placeholder: "www.company.com", value: contact.website, onChange: (e) => setContact((p) => ({ ...p, website: e.target.value })) })), /* @__PURE__ */ React.createElement(Field, { label: "Address" }, /* @__PURE__ */ React.createElement(IconInput, { icon: MapPin, placeholder: "City, Country", value: contact.address, onChange: (e) => setContact((p) => ({ ...p, address: e.target.value })) }))), activeFormTab === "cta" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Field, { label: "CTA Heading" }, /* @__PURE__ */ React.createElement("input", { className: inputClass, placeholder: "Ready to get started?", value: cta.heading, onChange: (e) => setCta((p) => ({ ...p, heading: e.target.value })) })), /* @__PURE__ */ React.createElement(Field, { label: "CTA Subtext" }, /* @__PURE__ */ React.createElement("input", { className: inputClass, placeholder: "Talk to our team today.", value: cta.subtext, onChange: (e) => setCta((p) => ({ ...p, subtext: e.target.value })) })), /* @__PURE__ */ React.createElement(Field, { label: "Button Text" }, /* @__PURE__ */ React.createElement("input", { className: inputClass, placeholder: "Get Started", value: cta.button, onChange: (e) => setCta((p) => ({ ...p, button: e.target.value })) }))), activeFormTab === "theme" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "flex gap-2" }, /* @__PURE__ */ React.createElement(
    "input",
    {
      className: inputClass,
      placeholder: "Describe a design style...",
      value: aiPrompt,
      onChange: (e) => setAiPrompt(e.target.value)
    }
  ), /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: () => generateAiTheme({ brand, product }),
      disabled: !aiPrompt.trim() || aiThemeLoading,
      className: "shrink-0 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition"
    },
    aiThemeLoading ? /* @__PURE__ */ React.createElement(Loader2, { size: 15, className: "animate-spin" }) : /* @__PURE__ */ React.createElement(Wand2, { size: 15 })
  )), aiThemeError && /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2 text-xs text-red-400 border border-red-500/20 bg-red-500/[0.06] rounded-xl px-3 py-2.5" }, /* @__PURE__ */ React.createElement(AlertCircle, { size: 13, className: "shrink-0" }), " ", aiThemeError), aiTheme && /* @__PURE__ */ React.createElement("div", { className: "flex items-center justify-between text-[11px] text-gray-400" }, /* @__PURE__ */ React.createElement("span", null, "Applied: ", /* @__PURE__ */ React.createElement("span", { className: "text-orange-300 font-semibold" }, aiTheme.themeName || "Custom Theme")), /* @__PURE__ */ React.createElement("button", { type: "button", onClick: handleResetTheme, className: "inline-flex items-center gap-1 text-gray-400 hover:text-white transition" }, /* @__PURE__ */ React.createElement(RotateCcw, { size: 12 }), " Reset")), /* @__PURE__ */ React.createElement("div", { className: "pt-1" }, /* @__PURE__ */ React.createElement("p", { className: "text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2" }, "Font Style"), /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-2 gap-2" }, FONT_OPTIONS.map((f) => {
    const isActive = selectedFontId === f.id;
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        key: f.id,
        type: "button",
        onClick: () => setSelectedFontId(f.id),
        className: `text-left rounded-lg border px-3 py-2 transition ${isActive ? "border-orange-500/60 bg-orange-500/[0.08]" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`,
        style: f.stack ? { fontFamily: f.stack } : void 0
      },
      /* @__PURE__ */ React.createElement("span", { className: `text-[13px] ${isActive ? "text-orange-300" : "text-gray-200"}` }, f.label)
    );
  })), selectedFontId === "other" && /* @__PURE__ */ React.createElement(
    "input",
    {
      className: `${inputClass} mt-2.5`,
      placeholder: "Type any font name, e.g. Nunito, Georgia, Roboto Slab...",
      value: customFontName,
      onChange: (e) => setCustomFontName(e.target.value),
      style: { fontFamily: selectedFontStack }
    }
  ))))))), /* @__PURE__ */ React.createElement("div", { className: "space-y-3" }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center justify-between" }, /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2" }, /* @__PURE__ */ React.createElement(FileText, { size: 14, className: "text-orange-500" }), /* @__PURE__ */ React.createElement("h4", { className: "text-white text-sm font-semibold" }, "Live Preview"), /* @__PURE__ */ React.createElement("span", { className: "text-[10.5px] font-medium text-gray-300 bg-white/[0.06] border border-white/10 rounded-full px-2.5 py-1" }, effectivePageCount ? `${effectivePageCount} page${effectivePageCount === 1 ? "" : "s"}` : "Single page"), /* @__PURE__ */ React.createElement("span", { className: "text-[10.5px] font-medium text-orange-300 bg-orange-500/10 border border-orange-500/20 rounded-full px-2.5 py-1" }, FLYER_TEMPLATES.find((t) => t.id === template)?.label, " template")), /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2" }, /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: () => window.print(),
      title: "Print directly from the browser \u2014 every page prints as its own true-size A4 sheet",
      className: "inline-flex items-center gap-2 border border-white/10 hover:border-orange-400/40 text-gray-300 hover:text-orange-300 text-xs font-semibold rounded-xl px-4 py-2.5 transition"
    },
    /* @__PURE__ */ React.createElement(FileText, { size: 14 }),
    " Print"
  ), /* @__PURE__ */ React.createElement("div", { className: "relative" }, /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: () => setDownloadMenuOpen((v) => !v),
      disabled: downloadingPreview,
      className: "inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs font-semibold rounded-xl px-4 py-2.5 transition"
    },
    downloadingPreview ? /* @__PURE__ */ React.createElement(Loader2, { size: 14, className: "animate-spin" }) : /* @__PURE__ */ React.createElement(Download, { size: 14 }),
    downloadingPreview ? `Exporting ${downloadFormat.toUpperCase()}...` : "Download",
    !downloadingPreview && /* @__PURE__ */ React.createElement(ChevronDown, { size: 14 })
  ), downloadMenuOpen && !downloadingPreview && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "fixed inset-0 z-10", onClick: () => setDownloadMenuOpen(false) }), /* @__PURE__ */ React.createElement("div", { className: "absolute right-0 mt-2 w-40 bg-black border border-orange-600/30 rounded-xl overflow-hidden shadow-xl z-20" }, /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: () => handleDownloadPreview("png"),
      className: "w-full text-left px-4 py-3 text-sm font-semibold text-white hover:bg-orange-500/10 transition"
    },
    "PNG Image"
  ), /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: () => handleDownloadPreview("pdf"),
      className: "w-full text-left px-4 py-3 text-sm font-semibold text-white hover:bg-orange-500/10 transition border-t border-orange-600/10"
    },
    "PDF Document"
  )))))), /* @__PURE__ */ React.createElement("div", { id: "flyer-print-root", ref: previewScrollRef, className: "thin-scrollbar rounded-2xl border border-white/5 bg-black/40 p-6 overflow-y-auto", style: { maxHeight: previewMaxHeight ? `${previewMaxHeight}px` : void 0 } }, /* @__PURE__ */ React.createElement(FitCard, { maxHeight: previewMaxHeight, designWidth: 0, minScale: 0.8, heightOffset: 56, fitFirstPage: true }, /* @__PURE__ */ React.createElement("div", { ref: previewRef, className: "w-fit mx-auto" }, /* @__PURE__ */ React.createElement(
    FlyerPreview,
    {
      layout,
      template,
      theme: activeTheme,
      brand,
      product,
      problemStatement,
      solutionStatement,
      features,
      benefits,
      whyChooseUs,
      screenshots,
      pricingEnabled,
      pricingPlans,
      testimonials,
      contact,
      cta,
      logoPreview
    }
  ))))));
};
const PremiumModuleCard = ({ icon: Icon, eyebrow, title, description, status = "Ready", onOpen }) => /* @__PURE__ */ React.createElement(
  "div",
  {
    onClick: onOpen,
    role: "button",
    tabIndex: 0,
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") onOpen();
    },
    className: "group relative cursor-pointer overflow-hidden rounded-[20px] border border-white/[0.1] bg-gradient-to-br from-orange-500/[0.07] via-white/[0.035] to-transparent p-6 backdrop-blur-2xl transition-all duration-300 hover:-translate-y-1 hover:border-orange-400/45 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_24px_48px_-24px_rgba(0,0,0,0.65),0_12px_28px_-14px_rgba(255,107,0,0.22)] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_32px_64px_-20px_rgba(255,107,0,0.42)]"
  },
  /* @__PURE__ */ React.createElement("div", { className: "pointer-events-none absolute -top-16 -right-10 h-40 w-40 rounded-full bg-orange-500/25 blur-[70px] opacity-60 transition-opacity duration-500 group-hover:opacity-100" }),
  /* @__PURE__ */ React.createElement("div", { className: "pointer-events-none absolute -bottom-16 -left-10 h-32 w-32 rounded-full bg-orange-500/20 blur-[60px] opacity-35 transition-opacity duration-500 group-hover:opacity-100" }),
  /* @__PURE__ */ React.createElement("div", { className: "pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/45 to-transparent" }),
  /* @__PURE__ */ React.createElement("div", { className: "pointer-events-none absolute right-5 top-5 h-3 w-3 border-r border-t border-orange-300/30 transition-colors duration-300 group-hover:border-orange-300/60" }),
  /* @__PURE__ */ React.createElement("div", { className: "relative flex items-center justify-between" }, /* @__PURE__ */ React.createElement("div", { className: "flex h-12 w-12 items-center justify-center rounded-2xl border border-orange-400/25 bg-orange-500/[0.12] shadow-[0_0_24px_-6px_rgba(255,107,0,0.45)] backdrop-blur-md transition-all duration-300 group-hover:border-orange-400/50 group-hover:bg-orange-500/[0.22] group-hover:shadow-[0_0_32px_-4px_rgba(255,107,0,0.6)]" }, /* @__PURE__ */ React.createElement(Icon, { size: 19, className: "text-orange-300 transition-colors duration-300 group-hover:text-orange-200" })), /* @__PURE__ */ React.createElement("span", { className: "text-[10px] font-medium uppercase tracking-[0.2em] text-orange-200/50" }, eyebrow)),
  /* @__PURE__ */ React.createElement("h3", { className: "relative mt-5 text-[18px] font-semibold tracking-tight text-white" }, title),
  /* @__PURE__ */ React.createElement("p", { className: "relative mt-2 text-[13.5px] leading-relaxed text-zinc-400" }, description),
  /* @__PURE__ */ React.createElement("div", { className: "relative mt-6 flex items-center justify-between border-t border-white/[0.08] pt-4" }, /* @__PURE__ */ React.createElement("span", { className: "flex items-center gap-1.5 text-[11px] font-medium text-zinc-400" }, /* @__PURE__ */ React.createElement("span", { className: "h-1.5 w-1.5 rounded-full bg-emerald-500/80" }), status), /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: (e) => {
        e.stopPropagation();
        onOpen();
      },
      className: "flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-[12px] font-medium text-zinc-200 transition-all duration-300 group-hover:border-orange-400/40 group-hover:bg-orange-500/[0.14] group-hover:text-orange-300"
    },
    "Open",
    /* @__PURE__ */ React.createElement(ArrowUpRight, { size: 13, className: "transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" })
  ))
);
const MediaSection = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState(null);
  useEffect(() => {
    const timers = /* @__PURE__ */ new WeakMap();
    const onScroll = (e) => {
      const el = e.target;
      if (!(el instanceof Element) || !el.classList.contains("thin-scrollbar")) return;
      el.classList.add("is-scrolling");
      window.clearTimeout(timers.get(el));
      timers.set(el, window.setTimeout(() => el.classList.remove("is-scrolling"), 900));
    };
    document.addEventListener("scroll", onScroll, true);
    return () => document.removeEventListener("scroll", onScroll, true);
  }, []);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("style", null, `
                .thin-scrollbar { scrollbar-width: thin; scrollbar-color: transparent transparent; }
                .thin-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
                .thin-scrollbar::-webkit-scrollbar-track { background: transparent; border-radius: 9999px; transition: background-color 0.25s ease; }
                .thin-scrollbar::-webkit-scrollbar-thumb { background-color: transparent; border-radius: 9999px; transition: background-color 0.25s ease; }
                .thin-scrollbar.is-scrolling { scrollbar-color: #f97316 rgba(249, 115, 22, 0.08); }
                .thin-scrollbar.is-scrolling::-webkit-scrollbar-track { background-color: rgba(249, 115, 22, 0.08); }
                .thin-scrollbar.is-scrolling::-webkit-scrollbar-thumb { background-image: linear-gradient(180deg, #fb923c, #ea580c); background-color: #f97316; box-shadow: 0 0 8px rgba(249, 115, 22, 0.45); }
            `), /* @__PURE__ */ React.createElement("div", { id: "sec-media", className: "thin-scrollbar h-full flex flex-col py-6 overflow-x-hidden overflow-y-auto lg:overflow-y-hidden pr-1" }, activeView === null && /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: () => navigate("/app/dashboard-preview"),
      className: "shrink-0 self-start mb-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-white/5 border border-white/10 text-gray-300 hover:text-orange-400 hover:border-orange-600/40 hover:bg-orange-600/10 transition"
    },
    /* @__PURE__ */ React.createElement(ArrowLeft, { size: 15 }),
    " Back"
  ), /* @__PURE__ */ React.createElement("div", { className: "shrink-0" }, /* @__PURE__ */ React.createElement("h1", { className: "text-2xl sm:text-3xl font-bold text-white flex items-center gap-2" }, /* @__PURE__ */ React.createElement(ImageIcon, { className: "text-orange-500", size: 26 }), "Media"), /* @__PURE__ */ React.createElement("p", { className: "text-gray-400 mt-1.5 text-sm" }, "Manage images, videos, and other assets used across your campaigns.")), activeView === null && /* @__PURE__ */ React.createElement("div", { className: "mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl shrink-0" }, /* @__PURE__ */ React.createElement(
    PremiumModuleCard,
    {
      icon: Sparkles,
      eyebrow: "Design Tool",
      title: "Feature Card",
      description: "Create and manage feature highlight cards for your products.",
      onOpen: () => setActiveView("feature")
    }
  ), /* @__PURE__ */ React.createElement(
    PremiumModuleCard,
    {
      icon: FileText,
      eyebrow: "Auto-Generate",
      title: "Flyer Generation",
      description: "Generate shareable flyers for your products automatically.",
      onOpen: () => setActiveView("flyer")
    }
  )), activeView === "feature" && /* @__PURE__ */ React.createElement("div", { className: "flex-1 min-h-0 flex flex-col" }, /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: () => setActiveView(null),
      className: "mt-6 shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-orange-400 transition w-fit"
    },
    /* @__PURE__ */ React.createElement(ArrowLeft, { size: 14 }),
    " Back"
  ), /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2 mt-4 shrink-0" }, /* @__PURE__ */ React.createElement(Sparkles, { size: 16, className: "text-orange-500" }), /* @__PURE__ */ React.createElement("h2", { className: "text-sm font-semibold text-white" }, "Feature Card")), /* @__PURE__ */ React.createElement(FeatureCardView, null)), activeView === "flyer" && /* @__PURE__ */ React.createElement("div", { className: "flex-1 min-h-0 flex flex-col" }, /* @__PURE__ */ React.createElement(
    "button",
    {
      type: "button",
      onClick: () => setActiveView(null),
      className: "mt-6 shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-orange-400 transition w-fit"
    },
    /* @__PURE__ */ React.createElement(ArrowLeft, { size: 14 }),
    " Back"
  ), /* @__PURE__ */ React.createElement("div", { className: "flex items-center gap-2 mt-4 shrink-0" }, /* @__PURE__ */ React.createElement(FileText, { size: 16, className: "text-orange-500" }), /* @__PURE__ */ React.createElement("h2", { className: "text-sm font-semibold text-white" }, "Flyer Generation")), /* @__PURE__ */ React.createElement(FlyerGenerationView, null))));
};
var Mediasection_default = MediaSection;
export {
  Mediasection_default as default
};